import type { Translation } from '../../types';
import type { common as en } from '../en/common';

export const common: Translation<typeof en> = {
  app: { name: 'Trung tâm điều hành Registrar', short: 'Control Center', skip: 'Chuyển tới nội dung' },
  env: { mock: 'Dữ liệu mẫu', mockHint: 'Dữ liệu mẫu tạo ngay trong trình duyệt, không phải dữ liệu thật' },
  deployment: {
    ote: 'OT&E',
    production: 'Production',
    label: 'Máy chủ: {{env}}',
    banner: 'Môi trường thử nghiệm (OT&E)',
    bannerDetail: 'Đây là máy chủ thử nghiệm: dữ liệu là dữ liệu thử và lệnh được gửi tới hệ thống thử nghiệm của các nhà quản lý tên miền.',
  },
  sidebar: { toggle: 'Ẩn/hiện thanh bên', title: 'Điều hướng', description: 'Điều hướng chính của hệ thống.' },
  search: {
    placeholder: 'Tìm tên miền, ID, clTRID/svTRID, mã yêu cầu hoặc trace ID…',
    label: 'Tìm kiếm toàn hệ thống',
    looksLike: 'Có vẻ là:',
    go: 'Tìm',
    commandTitle: 'Đi tới',
    commandPlaceholder: 'Gõ tên trang hoặc từ khoá tìm kiếm…',
    commandEmpty: 'Không có trang nào khớp.',
    searchFor: 'Tìm “{{q}}”',
    pages: 'Trang',
  },
  user: {
    operator: 'Nhân viên',
    signedIn: 'Nhân viên đang đăng nhập',
    user: 'Người dùng',
    loadFailed: 'Không tải được thông tin tài khoản',
    mfaVerified: 'Đã xác thực MFA',
    noMfa: 'Chưa MFA',
    noMfaHint: 'Các thao tác nhạy cảm (tranh chấp, quyết định và cung cấp dữ liệu) cần phiên đã xác thực MFA',
    expires: 'Phiên hết hạn {{at}}',
    reauth: 'Đăng nhập lại bằng TOTP',
    signOut: 'Đăng xuất',
    signingOut: 'Đang đăng xuất…',
    roles: 'Vai trò',
  },
  theme: { label: 'Giao diện', light: 'Sáng', dark: 'Tối', system: 'Theo hệ thống' },
  lang: { label: 'Ngôn ngữ', vi: 'Tiếng Việt', en: 'English' },
  nav: { open: 'Mở menu', close: 'Đóng menu', collapse: 'Thu gọn thanh bên', expand: 'Mở rộng thanh bên', home: 'Trang chủ' },
  state: {
    loading: 'Đang tải…',
    empty: 'Chưa có dữ liệu',
    error: 'Đã xảy ra lỗi',
    retry: 'Thử lại',
    code: 'Mã lỗi',
    requestId: 'Mã yêu cầu',
    copy: 'Sao chép {{value}}',
    copied: 'Đã sao chép',
    unknown: 'Không rõ',
    none: '—',
  },
  action: {
    refresh: 'Làm mới',
    cancel: 'Huỷ',
    save: 'Lưu',
    submit: 'Gửi',
    close: 'Đóng',
    back: 'Quay lại',
    next: 'Sau',
    previous: 'Trước',
    older: 'Cũ hơn',
    newest: 'Mới nhất',
    apply: 'Áp dụng',
    clear: 'Xoá',
    reset: 'Xoá bộ lọc',
    view: 'Xem',
    open: 'Mở',
    filter: 'Lọc',
    all: 'Tất cả',
    more: 'Thêm',
    export: 'Xuất',
  },
  table: { rows: '{{count}} dòng', showing: 'Đang hiển thị {{count}}', noResults: 'Không có kết quả khớp bộ lọc.' },
  time: { generated: 'Tạo lúc {{at}}', updated: 'Cập nhật {{at}}', ago: '{{value}} trước', in: 'sau {{value}}' },
  placeholder: {
    title: 'Sẽ có ở giai đoạn {{phase}}',
    body: 'Trang này được lên kế hoạch cho giai đoạn {{phase}} ({{name}}).',
  },
  errors: {
    notFound: 'Không tìm thấy trang',
    notFoundBody: 'Không có trang nào ở địa chỉ này.',
    goHome: 'Về trang chính',
    routeError: 'Trang này bị lỗi khi hiển thị',
    nothingAt: 'Không có trang nào ở {{path}}. Kiểm tra lại địa chỉ, hoặc dùng ô tìm kiếm hay menu để tìm nội dung cần xem.',
    crashed: 'Trang này gặp lỗi',
    crashedHint: 'Các phần khác của hệ thống vẫn hoạt động. Hãy tải lại trang; nếu vẫn lỗi, báo cáo kèm request ID.',
    reload: 'Tải lại trang',
  },
  phase: {
    title: '{{what}} dự kiến có ở giai đoạn {{phase}}: {{name}}',
    thisPage: 'Trang này',
    scope: 'Phạm vi giai đoạn {{phase}}: {{summary}}',
    filters: 'Khi có, trang sẽ mở với các bộ lọc sau:',
    overview: 'Tổng quan vận hành',
    domains: 'Tên miền',
    names: {
      '0': 'Nền tảng',
      '1': 'Khách hàng / Chủ thể / Hồ sơ',
      '2': 'Registry / EPP',
      '3': 'Tên miền',
      '4': 'Thương mại',
      '5': 'VNNIC',
      '6': 'ICANN / Dữ liệu đăng ký',
      '7': 'Chuyển đổi nhà đăng ký / Hết hạn / Khôi phục',
      '8': 'Đấu giá',
      '9': 'Đặt giữ tên miền / Drop catch',
      '10': 'Control Center đầy đủ / Giám sát',
      '11': 'Gia cố hệ thống',
    },
    summaries: {
      '0': 'Nền tảng: workflow, nhật ký kiểm toán và lịch sử, bộ máy chính sách, registry giả lập và khung Control Center.',
      '1': 'Khách hàng, chủ thể và phiên bản, tài liệu, xác minh danh tính, hồ sơ.',
      '2': 'EPP, kết nối registry, giao dịch registry, thông báo poll, đối soát.',
      '3': 'Tên miền, vòng đời, đăng ký, gia hạn, máy chủ tên/host, DNSSEC, lịch sử, dòng thời gian.',
      '4': 'Giá registry, bảng giá, báo giá, đơn hàng, sổ cái, tạm giữ, thanh toán, hoá đơn/hoàn tiền.',
      '5': 'Trung tâm tuân thủ VNNIC, đồng bộ hồ sơ, dữ liệu báo cáo, 0805A/0805B, đối soát.',
      '6': 'RDAP, WHOIS, chính sách công bố, cung cấp dữ liệu, RDE, lạm dụng, tranh chấp.',
      '7': 'Chuyển đổi nhà đăng ký (đến/đi), đổi chủ thể, chính sách hết hạn, ân hạn/chuộc lại, khôi phục/xoá.',
      '8': 'Đấu giá, đặt giá tuần tự, tạm giữ, tất toán, hiển thị thời gian thực.',
      '9': 'Đặt giữ tên miền, quy tắc ưu tiên, năng lực EPP riêng, tất toán.',
      '10': 'Metric, log, trace, cảnh báo, sự cố, runbook và liên kết giữa chúng.',
      '11': 'Kiểm thử tải, chaos, HA/DR, rà soát bảo mật và quyền riêng tư, OT&E/kiểm tra tuân thủ.',
    },
  },
  login: {
    docTitle: 'Đăng nhập',
    subtitle: 'Đăng nhập dành cho nhân viên',
    intro: 'Đăng nhập bằng tài khoản công việc để quản lý tên miền, registry và tuân thủ.',
    email: 'Email công việc',
    password: 'Mật khẩu',
    totp: 'Mã xác thực (TOTP)',
    totpHint: 'Bắt buộc với tài khoản đã bật MFA. Không nhập mã thì phiên đăng nhập không thực hiện được thao tác nhạy cảm (tranh chấp, quyết định và cung cấp dữ liệu).',
    submit: 'Đăng nhập',
    submitting: 'Đang đăng nhập…',
    expired: 'Phiên làm việc đã kết thúc hoặc bị máy chủ từ chối. Vui lòng đăng nhập lại.',
    needsMfa: 'Thao tác bạn vừa thực hiện cần phiên đã xác thực MFA. Hãy đăng nhập kèm mã xác thực.',
    mock: 'Chế độ dữ liệu mẫu: đăng nhập được giả lập. Email bất kỳ và mật khẩu từ 8 ký tự đều hợp lệ; nhập thêm mã 6 số bất kỳ để có phiên đã xác thực MFA. Email bắt đầu bằng {{locked}} sẽ hiển thị lỗi khoá tài khoản; bắt đầu bằng “customer” sẽ đăng nhập như người dùng của khách hàng.',
    failed: 'Đăng nhập thất bại',
    requestFailed: 'Yêu cầu không thành công.',
    invalidTitle: 'Thông tin đăng nhập không đúng',
    invalidBody: 'Email, mật khẩu hoặc mã xác thực không đúng. Tài khoản đã bật MFA phải nhập mã 6 số hiện tại; nếu thiếu, đăng nhập cũng bị từ chối với thông báo này.',
    lockedTitle: 'Tài khoản tạm thời bị khoá',
    lockedBody: 'Đăng nhập sai quá nhiều lần, hoặc tài khoản không còn hoạt động. Chờ hết thời gian khoá hoặc liên hệ quản trị viên.',
    networkTitle: 'Không kết nối được máy chủ',
    networkBody: 'Kiểm tra kết nối mạng rồi thử lại. Nếu vẫn không được, hãy liên hệ đội vận hành nền tảng.',
    otherBody: '{{message}} ({{code}}, request {{requestId}})',
    footer: 'Mọi lần đăng nhập đều được ghi vào nhật ký kiểm toán.',
    demo: 'Đăng nhập nhanh (OT&E)',
    demoHint: 'Máy chủ thử nghiệm: vào ngay bằng tài khoản {{email}}, không cần mật khẩu và mã xác thực.',
    demoLimited: 'Đăng nhập nhanh quá nhiều lần từ địa chỉ này. Chờ một phút rồi thử lại.',
  },
  searchPage: {
    title: 'Tìm kiếm',
    docTitle: 'Tìm kiếm: {{q}}',
    subtitle: 'Tra cứu tên miền hoặc bất kỳ định danh nào trên toàn nền tảng.',
    empty: 'Nhập từ khoá vào ô tìm kiếm phía trên.',
    interpreted: 'Cách hiểu truy vấn',
    confidence: { high: 'Độ tin cậy cao', medium: 'Độ tin cậy trung bình', low: 'Độ tin cậy thấp' },
    results: 'Kết quả',
    searching: 'Đang tìm…',
    failed: 'Tìm kiếm thất bại',
    noMatches: 'Không có kết quả',
    noMatchesBody: 'Không có kết quả cho {{q}}. ID, clTRID và svTRID phải khớp chính xác; tên miền phải khớp đúng tên.',
    correlation: 'Nếu đây là correlation ID, {{link}} hiển thị mọi sự kiện, workflow, giao dịch registry và bản ghi kiểm toán liên quan.',
    correlationLink: 'trang Correlation',
    accepts: 'Có thể tìm theo',
    acceptsList: {
      domains: 'Tên miền, dạng Unicode (ví-dụ.vn) hoặc Punycode (xn--v-d-rma6749a.vn)',
      uuids: 'UUID (ID thực thể, request, correlation, workflow; UUIDv7 cho biết thời điểm tạo) và ROID',
      trids: 'EPP clTRID / svTRID',
      ids: 'Mã yêu cầu (request ID), correlation ID, trace ID (32 ký tự hex hoặc traceparent) và workflow ID',
      parties: 'ID khách hàng và chủ thể',
    },
    shortcut: 'Nhấn {{key}} ở bất kỳ đâu để chuyển tới ô tìm kiếm.',
    kinds: {
      domain: 'Tên miền',
      domain_event: 'Sự kiện',
      registry_transaction: 'Giao dịch registry',
      workflow: 'Workflow',
      subject: 'Chủ thể',
      customer: 'Khách hàng',
    },
    hit: {
      domain: 'Tên miền',
      domainState: 'Tên miền · {{state}}',
      event: 'Sự kiện lịch sử tên miền (khớp theo request ID hoặc correlation ID)',
      registryTx: 'Giao dịch registry',
      workflow: 'Workflow',
      subject: 'Chủ thể ({{kind}})',
      customer: 'Khách hàng {{id}}',
    },
    cls: {
      traceparent: 'W3C traceparent',
      traceparentDetail: 'Trace ID {{id}}',
      uuid: 'UUID (v{{version}})',
      uuidDetail: 'ID thực thể, request, correlation, workflow hoặc giao dịch registry',
      uuidMinted: 'tạo lúc {{at}} · ID thực thể, request, correlation, workflow hoặc giao dịch registry',
      traceId: 'Trace ID',
      traceIdDetail: 'Trace ID W3C 128 bit',
      email: 'Địa chỉ email',
      emailDetail: 'Tra cứu khách hàng hoặc liên hệ',
      domain: 'Tên miền',
      domainIdnUnicode: 'Tên miền (IDN dạng Unicode)',
      domainIdnPunycode: 'Tên miền (IDN dạng Punycode)',
      trid: 'Mã giao dịch EPP (clTRID / svTRID)',
      tridDetail: 'Mã giao dịch phía nhà đăng ký (clTRID) hoặc phía registry (svTRID) của một lệnh EPP',
      roid: 'Mã đối tượng registry (ROID)',
      roidDetail: 'Mã định danh đối tượng do registry cấp',
      numeric: 'Mã số',
      numericDetail: 'Số đơn hàng, hoá đơn hoặc khách hàng',
      freeText: 'Văn bản tự do',
      freeTextDetail: 'Tìm theo tên trong tên miền, khách hàng và chủ thể',
    },
    actions: {
      openTrace: 'Mở trace',
      logsByRequest: 'Tìm log theo request ID',
      openWorkflow: 'Mở dạng workflow',
      openRegistryTx: 'Mở dạng giao dịch registry',
      customers: 'Khách hàng',
      openDomain: 'Mở tên miền',
      findRegistryTx: 'Tìm giao dịch registry',
      domains: 'Tên miền',
      orders: 'Đơn hàng',
      invoices: 'Hoá đơn',
    },
  },
  // Reasons the platform writes on records (lib/systemReason.ts).
  systemReason: {
    reportingRestart: 'Bên nhận báo cáo bắt đầu đồng bộ lại từ đầu',
    reportingEpochChanged: 'Bên nhận đổi phiên đồng bộ, đồng bộ lại từ đầu',
    reportingProjectionChanged: 'Dữ liệu báo cáo đổi cấu trúc, đồng bộ lại từ đầu',
    reportingWeeklyResweep: 'Đồng bộ lại định kỳ hằng tuần',
    reportingSuspended: 'Khoá báo cáo bị tạm ngưng',
    reportingExposedInUrl: 'Khoá báo cáo bị lộ trong đường dẫn (URL)',
    reportingBudgetExceeded: 'Vượt hạn mức truy vấn báo cáo',
    reportingNetworkNotAllowed: 'Gọi từ mạng không được phép',
    postRegistrationLock: 'Khoá chuyển nhượng sau khi đăng ký (chính sách chuyển đổi)',
    postTransferLock: 'Khoá chuyển nhượng sau khi chuyển về (chính sách chuyển đổi)',
    changeOfRegistrantLock: 'Khoá chuyển nhượng sau khi đổi chủ thể (chính sách chuyển đổi)',
    registrarLock: 'Khoá chuyển nhượng do chủ thể hoặc nhân viên đặt',
    unlockedByRegistrant: 'Chủ thể đã mở khoá',
    lockExpired: 'Khoá có thời hạn đã hết hạn',
    nameChangeLock: 'Hoàn tất đổi tên: khoá toàn bộ phía nhà đăng ký (như VNNIC làm sau khi đăng ký)',
    registryLockRemoved: 'Đã gỡ khoá tại registry',
    confirmedByStaff: 'Nhân viên đã xác nhận',
    checkAtRegistry: 'Kiểm tra lại tại registry',
    registryShowsChange: 'Registry đã thể hiện thay đổi (domain:info)',
    nameChangeCancelled: 'Yêu cầu đổi tên đã huỷ',
    registrationCancelled: 'Nhân viên đã huỷ đăng ký',
    registrationResumed: 'Nhân viên đã cho đăng ký chạy lại',
    registrationReactivated: 'Nhân viên đã kích hoạt lại đơn đăng ký',
    registrationEndedUncreated: 'Đăng ký đã kết thúc, tên miền không được tạo',
    domainRegistered: 'Tên miền đã đăng ký thành công',
    domainDeleted: 'Tên miền đã bị xoá',
    upstreamNoLonger: 'Nhà đăng ký cấp trên không còn báo việc này',
    submittedInPanel: 'Đã nộp trên hệ thống VNNIC',
    carryIdentity: 'Giữ nguyên thông tin định danh khi tạo phiên bản chủ thể mới',
    carryAccountIdentity: 'Chuyển thông tin định danh của tài khoản cũ sang chủ thể chính',
    carryCustomerIdentity: 'Chuyển thông tin định danh từ tài khoản khách hàng',
    ekycComparison: 'Đối chiếu kết quả eKYC (OCR) với thông tin chủ thể đã khai',
    ekycVerification: 'Xác thực eKYC tự động (VNNIC OCR / FaceID)',
    documentVerification: 'Kiểm tra giấy tờ tự động',
    hstmBackup: 'Sao lưu hồ sơ gửi VNNIC (HSTM)',
    businessRegistryLookup: 'Tra cứu cơ sở dữ liệu doanh nghiệp qua VNNIC',
    promotionCodeRequest: 'Xin mã khuyến mại từ VNNIC',
    declaration: 'Bản khai',
    declarationRepresentative: 'Bản khai: người đại diện theo pháp luật',
    declarationCheckGenerated: 'Kiểm tra bản khai: bản PDF đã tạo',
    declarationCheckUploaded: 'Kiểm tra bản khai: tệp đã tải lên',
    declarationDownload: 'Chủ thể tải bản khai của mình',
    dossierFileSignatureCheck: 'Tệp hồ sơ: kiểm tra chữ ký',
    registrarSignature: 'Nhà đăng ký ký tệp hồ sơ',
    signatureBackfill: 'Bổ sung chữ ký nhà đăng ký: bản khai đã ký và tệp được thay thế',
    proofEkyc: 'Thông tin chủ thể theo kết quả eKYC',
    proofDigitalSignature: 'Thông tin chủ thể theo tệp ký số đã kiểm tra chữ ký',
    acceptedWhenSigning: 'Nhà đăng ký duyệt khi ký',
    declarationSuperseded: 'Đã được thay bằng bản khai tải lên mới',
    declarationSignedManual: 'Nhà đăng ký đã ký bản khai tên miền (yêu cầu thủ công)',
    dossierPassesChecks: 'Hồ sơ đã đạt các kiểm tra trước khi ký',
    signatureRecorded: 'Chữ ký của nhà đăng ký đã được ghi nhận làm xác nhận',
    problemGone: 'Vấn đề không còn nữa',
    dossierClosed: 'Hồ sơ đã hoàn tất hoặc được thay thế, hoặc tên miền đã đóng',
    notificationResent: 'Thông báo đã được gửi lại',
    einvoiceIssued: 'Hoá đơn điện tử đã phát hành',
    contentChangedAfterCorrection: 'Nội dung đã thay đổi sau yêu cầu sửa hồ sơ',
    changeOfRegistrant: 'Thay đổi chủ thể tên miền',
    registrantCorrected: 'Nhân viên sửa thông tin chủ thể trước khi gửi lệnh tạo tên miền tới registry (thử lại)',
    primarySubjectCreated: 'Chủ thể chính được tạo cùng khách hàng',
    representativeWithCustomer: 'Người đại diện theo pháp luật nhập cùng khách hàng mới',
    representativeOnForm: 'Người đại diện theo pháp luật đặt trên biểu mẫu chủ thể',
    signInCreated: 'Tài khoản đăng nhập được tạo cùng tài khoản khách hàng',
    badPasswordOrCode: 'Sai mật khẩu hoặc mã xác thực',
    unknownAccount: 'Tài khoản không tồn tại',
    lockedOrInactive: 'Tài khoản đang bị khoá hoặc không hoạt động',
    mfaRequired: 'Cần xác thực hai lớp',
    mfa: 'Đăng nhập có xác thực hai lớp',
    passwordOnly: 'Đăng nhập chỉ bằng mật khẩu',
    quickSignIn: 'Đăng nhập nhanh (OT&E), không mật khẩu và mã xác thực',
    quickSignInMfa: 'Đăng nhập nhanh (OT&E), phiên được tính như đã xác thực hai lớp',
    wrongEmailOrPassword: 'Sai e-mail hoặc mật khẩu',
    wrongCode: 'Sai mã xác thực',
    tooManyWrongPasswords: 'Sai mật khẩu quá nhiều lần',
    tooManyFromAddress: 'Quá nhiều lần thử từ địa chỉ này',
    wrongDomainOrDnsPassword: 'Sai tên miền hoặc mật khẩu DNS',
    tooManyWrongDnsPasswords: 'Sai mật khẩu DNS quá nhiều lần từ địa chỉ này',
    emailNotVerified: 'E-mail chưa được xác minh',
    notAMember: 'Người dùng không thuộc tài khoản này',
    signedOut: 'Đã đăng xuất',
    captchaInvalid: 'Kết quả xác minh không phải robot không hợp lệ',
    captchaExpiredOrUsed: 'Kết quả xác minh không phải robot đã hết hạn hoặc đã được dùng',
    captchaWrongSite: 'Bước xác minh không phải robot được làm trên trang khác',
    captchaWrongForm: 'Bước xác minh không phải robot được làm cho biểu mẫu khác',
    registryRejectedRenewal: 'Registry từ chối gia hạn',
    // Journal entry descriptions (crates/billing, ordering, auction, backorder).
    paymentSettled: 'Thanh toán đã ghi vào ví',
    // Templates: {{1}}, {{2}}, … are the values, each read in turn (`value`, or a reason of its own).
    dispute: 'Tranh chấp {{2}} ({{1}}, {{3}})',
    nameChangeClosed: 'Yêu cầu đổi tên đã đóng: {{1}}',
    nameChangeEnded: 'Yêu cầu đổi tên {{1}}',
    panelOperation: 'Thao tác trên hệ thống VNNIC {{1}}: {{2}}',
    addonCancelled: 'Đã huỷ dịch vụ: {{1}}',
    registryRefusedLock: 'Registry từ chối khoá: {{1}}',
    passwordNext: 'Đúng mật khẩu; bước tiếp theo: {{1}}',
    secondFactor: 'Xác thực hai lớp bằng {{1}}',
    stepUp: 'Xác thực lại bằng {{1}}',
    dossierDecision: '{{1}}: {{2}} (cách xác minh: {{3}})',
    staffReview: 'Nhân viên yêu cầu sửa khi duyệt: {{2}} (việc {{1}})',
    nameChangeDossier: '{{1}} (yêu cầu đổi tên {{2}})',
    domainReturn: 'Hoàn trả tên miền (bản khai {{1}})',
    proofSubmission: '{{1}}: {{3}} {{4}} (lần nộp {{2}}, tệp {{5}})',
    proofAccepted: 'Đã chấp nhận bằng chứng danh tính (lần xác minh {{1}})',
    fileRecovered: 'Tự động nộp tệp {{1}} vào luồng duyệt: tệp tải lên lúc {{2}} (người tải lên: {{3}}) chưa được kiểm tra, quyết định hay nộp (quy tắc từ 06/10/2026: tải lên là nộp)',
    registryContact: 'Dùng cho liên hệ {{1}} tại registry',
    sessionsRevoked: 'Đã thu hồi {{1}} phiên đăng nhập',
    domainState: 'Tên miền đang ở trạng thái {{1}}',
    cannotRenew: 'Tên miền đang ở trạng thái {{1}} nên không gia hạn được (nếu đang trong thời gian chuộc lại thì khôi phục)',
    registryCost: 'Giá vốn registry của {{1}}',
    auction: 'Đấu giá {{1}}',
    backorderCatch: 'Bắt được tên miền đặt trước {{1}}',
    bankTransfer: 'Chuyển khoản ngân hàng, mã thanh toán {{1}}',
    refundToPayer: 'Hoàn tiền về nguồn thanh toán: {{1}}',
    refundToPayerFailed: 'Hoàn tiền về nguồn không thành công, tiền trả lại ví',
    withReference: '{{1}} (mã tham chiếu {{2}})',
    // a hold capture: the order operation (commerce:enum.operation) and the domain name
    capture: '{{1}}: {{2}}',
    value: {
      rejected: 'bị từ chối',
      cancelled: 'đã huỷ',
      REFUSED: 'bị từ chối',
      CANCELLED: 'đã huỷ',
      MFA: 'nhập mã xác thực hai lớp',
      ENROL: 'cài đặt xác thực hai lớp',
      ACTIVE: 'đã đăng nhập',
      otp: 'mã xác thực (TOTP)',
      rcv: 'mã khôi phục',
      MANUAL: 'Nhân viên duyệt',
      AUTOMATIC: 'Tự động',
      reviewer_approved_all_requirements_met: 'nhân viên đã duyệt, đủ mọi yêu cầu',
      all_policy_requirements_met: 'đủ mọi yêu cầu của chính sách',
      PROOF_SUBMISSION: 'Nộp bằng chứng danh tính',
      registration: 'bản khai đăng ký tên miền',
      'subject declaration': 'bản khai chủ thể',
    },
    original: 'Nguyên văn: {{text}}',
  },
  // A registry command's result by its EPP result code (RFC 5730 §3; lib/eppResult.ts).
  eppResult: {
    success: 'Registry đã thực hiện lệnh.',
    failure: 'Registry từ chối lệnh.',
    1000: 'Registry đã thực hiện lệnh.',
    1001: 'Registry đã nhận lệnh, đang chờ xử lý.',
    1300: 'Không có thông báo nào trong hàng đợi.',
    1301: 'Đã nhận thông báo; cần xác nhận để lấy thông báo tiếp theo.',
    1500: 'Đã kết thúc phiên làm việc với registry.',
    2000: 'Registry không nhận ra lệnh.',
    2001: 'Lệnh sai cú pháp.',
    2002: 'Lệnh không dùng được trong hoàn cảnh này.',
    2003: 'Thiếu thông tin bắt buộc.',
    2004: 'Giá trị nằm ngoài phạm vi cho phép.',
    2005: 'Giá trị sai định dạng.',
    2100: 'Registry không hỗ trợ phiên bản giao thức này.',
    2101: 'Registry chưa hỗ trợ lệnh này.',
    2102: 'Registry chưa hỗ trợ tuỳ chọn này.',
    2103: 'Registry chưa hỗ trợ phần mở rộng này.',
    2104: 'Registry từ chối vì lỗi thanh toán (số dư tại registry).',
    2105: 'Đối tượng chưa đủ điều kiện gia hạn.',
    2106: 'Đối tượng chưa đủ điều kiện chuyển đổi nhà đăng ký.',
    2200: 'Registry từ chối xác thực.',
    2201: 'Nhà đăng ký không có quyền với đối tượng này.',
    2202: 'Mã chuyển đổi (auth info) không đúng.',
    2300: 'Đối tượng đang chờ chuyển đổi nhà đăng ký.',
    2301: 'Đối tượng không có yêu cầu chuyển đổi nào.',
    2302: 'Đối tượng đã tồn tại tại registry.',
    2303: 'Đối tượng không tồn tại tại registry.',
    2304: 'Trạng thái của đối tượng không cho phép thao tác này.',
    2305: 'Đối tượng đang được dùng ở nơi khác nên không thể thao tác.',
    2306: 'Giá trị không đúng chính sách của registry.',
    2307: 'Registry chưa hỗ trợ dịch vụ cho loại đối tượng này.',
    2308: 'Vi phạm chính sách quản lý dữ liệu của registry.',
    2400: 'Registry báo lỗi khi xử lý lệnh; kết quả chưa chắc chắn.',
    2500: 'Registry báo lỗi và đóng kết nối.',
    2501: 'Registry từ chối xác thực và đóng kết nối.',
    2502: 'Vượt giới hạn số phiên; registry đóng kết nối.',
  },
  apiErrors: {
    detail: 'Chi tiết kỹ thuật (gửi kèm khi báo lỗi)',
    codes: {
      ACCOUNT_LOCKED: 'Tài khoản đang bị khoá.',
      ACCOUNT_NOT_OPEN: 'Ví của khách hàng đang bị khoá hoặc đã đóng nên không ghi sổ được.',
      ADDONS_UNAVAILABLE: 'Tạm thời không liên lạc được với các tiện ích. Thử lại sau ít phút.',
      ADDON_BUSY: 'Tiện ích đang bận với nhiều việc cùng lúc. Thử lại sau ít phút.',
      ADDON_FAILED: 'Tiện ích gặp lỗi khi xử lý. Thử lại; nếu vẫn lỗi, báo người quản lý tiện ích.',
      ADDON_NOT_RUNNING: 'Tiện ích đang không chạy. Bật tiện ích ở Cài đặt › Module › Tiện ích.',
      ADDON_TIMEOUT: 'Tiện ích trả lời quá lâu nên đã bị dừng. Thử lại sau ít phút.',
      ANSWER_TOO_LARGE: 'Kết quả của tiện ích quá lớn để hiển thị. Thu hẹp bộ lọc rồi thử lại.',
      NOT_PARKED: 'Sự kiện này không còn tạm giữ (đã được gửi lại). Tải lại trang để xem trạng thái mới nhất.',
      NOT_PRODUCTION_READY: 'Thành phần này chỉ dùng để phát triển và kiểm thử, không dùng được trên máy chủ chính thức.',
      TOO_LARGE: 'Dữ liệu gửi cho tiện ích quá lớn.',
      TRY_AGAIN: 'Tiện ích tạm thời chưa làm được việc này. Thử lại sau ít phút.',
      UNKNOWN_ADDON: 'Không có tiện ích này, hoặc tiện ích chưa được cài trên máy chủ.',
      ADJUSTED_INVOICE: 'Hoá đơn này đã được điều chỉnh nên không thay thế hay huỷ được nữa; hãy điều chỉnh tiếp.',
      ADJUSTMENT_NOT_CORRECTED: 'Không điều chỉnh, thay thế hay huỷ chính hoá đơn điều chỉnh; hãy điều chỉnh tiếp hoá đơn gốc.',
      ALREADY_CLOSED: 'Việc này đã được đóng trước đó.',
      ALREADY_DISABLED: 'Mục này đã bị tắt trước đó.',
      ALREADY_LINKED: 'Mục này đã được liên kết.',
      APPROVAL_EXPIRED: 'Đề nghị duyệt đã hết hạn. Hãy gửi đề nghị mới.',
      APPROVAL_PENDING: 'Đề nghị đang chờ người thứ hai duyệt.',
      APPROVAL_REQUIRED: 'Thao tác này cần được phê duyệt trước.',
      APPROVAL_SELF: 'Người đề nghị không tự duyệt được đề nghị của mình.',
      APPROVAL_STALE: 'Thông tin xuất hoá đơn của khách hàng đã đổi sau khi duyệt. Hãy gửi đề nghị mới.',
      APPROVAL_USED: 'Đề nghị này đã được thực hiện rồi.',
      AWAITING_CONFIRMATION: 'Tên miền đang được bảo vệ: yêu cầu đã được ghi nhận và chờ nhân viên có thẩm quyền xác nhận.',
      CANCEL_NOT_ALLOWED: 'Cài đặt hoá đơn điện tử không cho huỷ hoá đơn; hãy điều chỉnh hoặc thay thế.',
      CHANGE_IN_PROGRESS: 'Đang có một thay đổi khác chưa xong. Chờ thay đổi đó hoàn tất rồi thử lại.',
      CHANGED_SINCE_PROPOSED: 'Nơi nhận đã thay đổi sau khi yêu cầu này được gửi duyệt. Hãy gửi duyệt lại.',
      CHANNEL_REQUIRED: 'Cần chọn kênh đăng ký.',
      CHECKS_FAILED: 'Kiểm tra trước khi thực hiện chưa đạt.',
      CODE_TAKEN: 'Mã này đã được dùng.',
      CONCURRENT_MODIFICATION: 'Dữ liệu vừa được người khác thay đổi. Tải lại trang rồi thử lại.',
      CONFLICT: 'Thao tác trùng với một thay đổi khác. Tải lại trang rồi thử lại.',
      CONFIRMATIONS_REQUIRED: 'Hãy duyệt ở Cài đặt › Kết nối › API báo cáo, nơi xác nhận ba điểm về hệ thống nhận dữ liệu.',
      CONTACTS_RULE: 'Việc này nay gửi email, điện thoại khách: hãy gửi duyệt lại để người có quyền xử lý dữ liệu cá nhân duyệt.',
      CREDIT_NOTE_NO_EINVOICE: 'Phiếu điều chỉnh không có hoá đơn điện tử riêng: hãy điều chỉnh giảm hoá đơn điện tử của hoá đơn gốc.',
      CREDIT_NOTE_USED: 'Phiếu điều chỉnh này đã được đưa lên một hoá đơn điều chỉnh.',
      DOMAIN_NOT_REGISTERED_HERE: 'Sản phẩm này chỉ nhận tên miền đăng ký tại Tino: đăng ký hoặc chuyển tên miền về trước, rồi tạo dịch vụ.',
      DOMAIN_OWN_NOT_ALLOWED: 'Sản phẩm này chưa cho dùng tên miền khách đã có, nên nhân viên chưa tạo được dịch vụ (chưa có giỏ hàng để đăng ký hay chuyển tên miền về). Bật “Cho dùng tên miền khách đã có” ở tab Tên miền của sản phẩm.',
      EINVOICE_PROVIDER_NOT_ANSWERING: 'Nhà cung cấp hoá đơn điện tử không trả lời. Thử lại sau.',
      EINVOICE_PROVIDER_REFUSED: 'Nhà cung cấp hoá đơn điện tử từ chối yêu cầu.',
      EINVOICE_PROVIDER_UNAVAILABLE: 'Chưa bật nhà cung cấp hoá đơn điện tử (Cài đặt › Module › Hoá đơn ĐT).',
      EINVOICE_SETTINGS_UNAVAILABLE: 'Không đọc được cài đặt hoá đơn điện tử đang áp dụng.',
      INCREASE_NOT_SUPPORTED: 'Chưa hỗ trợ điều chỉnh tăng: số tiền được điều chỉnh bằng phiếu điều chỉnh giảm.',
      IN_PROGRESS: 'Đang gọi nhà cung cấp cho hoá đơn này. Chờ kết quả rồi tải lại.',
      MAPPING_INVALID: 'Thiết lập mẫu Zalo chưa hợp lệ. Sửa các tham số được đánh dấu rồi lưu lại.',
      NOT_ACTIVE: 'Khoá không còn được dùng.',
      CONNECTION_RETIRED: 'Kết nối này đã ngừng dùng.',
      CUSTOMER_NOT_ACTIVE: 'Tài khoản khách hàng đang không hoạt động.',
      DECISION_REQUIRED: 'Cần chọn quyết định.',
      DESTINATION_NOT_ALLOWED: 'Địa chỉ phải bắt đầu bằng https và mở được từ Internet. Hệ thống chỉ chạy trong mạng nội bộ không nhận được tín hiệu: hãy để nó tự lấy dữ liệu theo lịch.',
      DECLARATIONS_DISABLED: 'Chức năng bản khai đang tắt.',
      DOCUMENTS_MISSING: 'Hồ sơ còn thiếu tài liệu.',
      DOMAIN_CHANGED: 'Tên miền vừa thay đổi trong lúc đọc registry nên chưa ghi gì. Thử lại sau giây lát.',
      DOMAIN_PROTECTED: 'Tên miền đang được bảo vệ nên không thực hiện được thao tác này trực tuyến.',
      DUPLICATE_REFERENCE: 'Mã giao dịch này đã được ghi nhận trước đó. Kiểm tra lại để không cộng tiền hai lần.',
      EXPORT_DISABLED: 'Chức năng xuất dữ liệu đang tắt.',
      FORBIDDEN: 'Bạn không có quyền thực hiện thao tác này.',
      FOUR_EYES_APPROVAL_REQUIRED: 'Thao tác này cần một nhân viên khác phê duyệt trước.',
      GATEWAY_UNAVAILABLE: 'Không kết nối được dịch vụ bên ngoài lúc này. Thử lại sau.',
      IDEMPOTENCY_KEY_REQUIRED: 'Yêu cầu thiếu thông tin chống gửi trùng. Tải lại trang rồi thử lại.',
      IDEMPOTENCY_KEY_REUSED: 'Biểu mẫu đã thay đổi sau lần gửi lỗi; gửi lại để tạo yêu cầu mới.',
      INDIVIDUAL_EKYC_ONLY: 'Cá nhân chỉ được xác minh bằng eKYC.',
      INSUFFICIENT_FUNDS: 'Số dư tài khoản của khách hàng không đủ cho thao tác này.',
      INTERNAL_ERROR: 'Máy chủ gặp lỗi khi xử lý. Thử lại; nếu vẫn lỗi, báo bộ phận kỹ thuật kèm mã yêu cầu.',
      INVALID: 'Thông tin nhập chưa hợp lệ. Kiểm tra lại các trường rồi gửi lại.',
      INVALID_CREDENTIALS: 'Email hoặc mật khẩu không đúng.',
      INVALID_CURSOR: 'Trang kết quả đã cũ. Tải lại danh sách.',
      INVALID_DATE: 'Ngày không hợp lệ.',
      KEYS_WITHOUT_ADDRESSES: 'Có khoá chưa giới hạn địa chỉ được dùng. Trước khi gửi email, điện thoại khách, hãy đổi các khoá đó (nhập địa chỉ được dùng) hoặc thu hồi chúng.',
      INVALID_DOMAIN: 'Tên miền không hợp lệ.',
      INVALID_QUERY: 'Bộ lọc không hợp lệ. Xoá bộ lọc rồi thử lại.',
      NOT_CANCELLABLE_NOW: 'Yêu cầu huỷ này không gửi được ở trạng thái hiện tại.',
      NOT_ISSUABLE_NOW: 'Hoá đơn điện tử ở trạng thái này không phát hành (lại) được.',
      NOT_ISSUED: 'Hoá đơn điện tử chưa được phát hành.',
      NOT_RECONCILING: 'Hoá đơn điện tử này không cần tra cứu theo khoá.',
      NOT_WITHDRAWABLE: 'Chỉ bỏ được hoá đơn điều chỉnh hoặc thay thế chưa phát hành.',
      ORIGINAL_NOT_WITHDRAWN: 'Không bỏ hoá đơn điện tử của chính hoá đơn; hãy huỷ hoặc thay thế nếu cần.',
      PARAMS_INCOMPLETE: 'Dữ liệu mẫu không điền đủ các tham số của mẫu Zalo.',
      PHONE_INVALID: 'Cần một số di động Việt Nam.',
      PROVIDER_CANNOT: 'Nhà cung cấp hoá đơn điện tử đang bật không làm được việc này.',
      PROVIDER_NOT_READY: 'Kết nối chưa sẵn sàng (chưa có mã truy cập). Xem tình trạng kết nối.',
      PROVIDER_REFUSED: 'Nhà cung cấp từ chối yêu cầu (mã lỗi trong chi tiết).',
      PROVIDER_UNREACHABLE: 'Không kết nối được nhà cung cấp lúc này. Thử lại sau.',
      MODULE_UNAVAILABLE: 'Kết nối chưa dùng được: bật hoặc cấu hình kết nối ở Cài đặt › Module.',
      MISSING_AT_PROVIDER: 'Máy chủ hosting không còn tài khoản này: hệ thống đã ghi nhận độ lệch. Kiểm tra trên WHM trước khi xử lý dịch vụ.',
      USERNAME_TAKEN: 'Tên đăng nhập này đã có tài khoản khác trên máy chủ hosting.',
      DOMAIN_TAKEN: 'Tên miền này đã có tài khoản khác trên máy chủ hosting.',
      SAME_FORM_REQUIRED: 'Hoá đơn đã được sửa bằng một hình thức (điều chỉnh hoặc thay thế); các lần sau phải dùng cùng hình thức đó.',
      UNEXPECTED_RESPONSE: 'Máy chủ trả lời không đúng dạng. Thử lại sau ít phút.',
      INVALID_STATE: 'Không thực hiện được ở trạng thái hiện tại của đối tượng. Tải lại trang để xem trạng thái mới nhất.',
      INVALID_STATUS: 'Không thực hiện được ở trạng thái hiện tại của đối tượng. Tải lại trang để xem trạng thái mới nhất.',
      MANDATORY_ADDON: 'Dịch vụ kèm theo này là bắt buộc nên không tắt được.',
      METHOD_NOT_ALLOWED: 'Chức năng này trên máy chủ đang dùng không nhận kiểu yêu cầu này. Cập nhật hệ thống rồi thử lại.',
      MFA_REQUIRED: 'Thao tác này cần phiên đăng nhập có mã xác thực.',
      NETWORK_ERROR: 'Không kết nối được tới máy chủ. Kiểm tra mạng rồi thử lại.',
      NOTHING_TO_SIGN: 'Không có tệp nào cần ký.',
      NOT_CONFIGURED: 'Kết nối cần cho thao tác này chưa được cấu hình.',
      NOT_FOUND: 'Không tìm thấy dữ liệu yêu cầu. Có thể nó đã bị xoá hoặc bạn không có quyền xem.',
      NOT_OFFERED: 'Đuôi tên miền này chưa được mở bán.',
      NO_CHANGE: 'Không có gì khác so với hiện tại.',
      NO_PRICE: 'Chưa có giá bán cho thao tác này. Thiết lập giá cho đuôi tên miền trước.',
      NO_TAX_CODE: 'Gói và nhóm của gói đều chưa có thuế suất VAT: đặt thuế cho nhóm hoặc cho gói trước.',
      HOURLY_NOT_BUILT: 'Gói bán theo giờ mới báo giá được, chưa đặt đơn được.',
      LOCATION_NOT_OFFERED: 'Gói này không mở vị trí đã chọn cho khách chọn. Chọn vị trí khác hoặc để hệ thống tự chọn.',
      OUT_OF_STOCK: 'Gói đã hết hàng ở vị trí này (kho đóng, đã bán hết hoặc chưa có kho). Chọn vị trí khác hoặc bổ sung kho cho gói.',
      OPTION_REQUIRED: 'Còn thiếu tuỳ chọn bắt buộc của gói. Chọn đủ các tuỳ chọn bắt buộc rồi gửi lại.',
      OPTION_UNKNOWN: 'Có tuỳ chọn không thuộc gói này hoặc không còn đặt được. Tải lại trang rồi chọn lại.',
      OPTION_NOT_ALLOWED: 'Có tuỳ chọn không chọn được cùng các lựa chọn khác, hoặc nội dung nhập quá dài.',
      OPTION_QUANTITY: 'Số lượng của tuỳ chọn nằm ngoài giới hạn hoặc không đúng bước tăng của gói.',
      HOURLY_PRICE_INVALID: 'Giá theo giờ chưa đúng: giá gói theo giờ không có phí khởi tạo; giá tuỳ chọn theo giờ chỉ nhập phần trăm (từ 0 đến 999,9999, tối đa 4 chữ số thập phân); chu kỳ khác không nhập phần trăm.',
      FULFILMENT_INVALID: 'Kết nối module của gói chưa đúng: bảng “Sẵn sàng bán” ở tab Kết nối module cho biết điều kiện nào chưa đạt.',
      FULFILMENT_CHANGED: 'Nhóm máy chủ hoặc kết nối module vừa được đổi trong lúc kiểm. Bấm Kiểm tra rồi lưu lại.',
      GROUP_MODULE_MISMATCH: 'Nhóm máy chủ dùng module khác: chọn nhóm của đúng module, hoặc kết nối của đúng module cho nhóm.',
      NOT_OVERRIDABLE: 'Gói không được ghi đè tham số này: giá trị luôn lấy theo nhóm máy chủ.',
      SETTINGS_INVALID: 'Tham số cấp phát chưa hợp lệ với module: xem trường bị báo trong chi tiết kỹ thuật.',
      MEMBER_NOT_FOUND: 'Không có kết nối này trong module của nhóm: chọn một kết nối có thật ở Cài đặt › Module.',
      MIXED_MEMBERS: 'Một kết nối chỉ thêm vào nhóm theo một cách: cả kết nối, hoặc từng máy chủ của nó.',
      MAPS_TO_UNKNOWN: 'Thông số nhận lựa chọn không có trong danh sách của loại dịch vụ hoặc của module mà các gói dùng.',
      OPTION_MODULE_MISMATCH: 'Kiểu tuỳ chọn này thuộc module mà các gói của sản phẩm không dùng.',
      SALE_MODE_MISMATCH: 'Không hợp với cách bán của nhóm: gói của nhóm bán theo giờ chỉ có chu kỳ 1 giờ, gói của nhóm theo chu kỳ không có 1 giờ, và sản phẩm đã có gói chỉ chuyển sang nhóm cùng cách bán.',
      OPERATION_BLOCKED: 'Thao tác đang bị chặn (khoá, bảo vệ hoặc trạng thái tên miền không cho phép).',
      PAYLOAD_TOO_LARGE: 'Dữ liệu gửi lên quá lớn. Bớt nội dung hoặc chọn tệp nhỏ hơn rồi gửi lại.',
      PENDING_CONFIRMATION: 'Đã gửi; registry chưa xác nhận kết quả. Hệ thống sẽ đối soát và cập nhật.',
      PRICE_CONFLICT: 'Đã có một giá khác áp dụng cùng thời gian.',
      QUOTE_UNUSABLE: 'Báo giá đã hết hạn hoặc không còn dùng được. Lấy báo giá mới.',
      RATE_LIMITED: 'Thao tác quá dày. Đợi một lát rồi thử lại.',
      REASON_REQUIRED: 'Cần nhập lý do cho thao tác này.',
      REGISTRIES_UNAVAILABLE: 'Không kết nối được registry lúc này.',
      REGISTRY_NOT_SENT: 'Lệnh chưa được gửi tới registry: kết nối đã dừng lệnh trước khi gửi (lý do của kết nối trong chi tiết kỹ thuật).',
      REGISTRY_REJECTED: 'Registry từ chối yêu cầu.',
      REGISTRY_SYNC_COOLDOWN: 'Tên miền vừa được đồng bộ với registry. Chờ khoảng 30 giây rồi thử lại.',
      REGISTRY_SYNC_IN_PROGRESS: 'Đang đọc tên miền này tại registry. Chờ lần đọc đó xong rồi tải lại trang.',
      REGISTRY_UNAVAILABLE: 'Không kết nối được registry lúc này.',
      REGISTRY_UNKNOWN: 'Registry chưa trả lời (kết quả chưa rõ), nên không biết có khớp hay không; chưa có gì thay đổi. Thử lại sau ít phút.',
      ALREADY_IN_SYSTEM: 'Tên miền này đã có trong hệ thống.',
      NOT_AT_REGISTRY: 'Registry không có tên miền này.',
      NOT_SPONSORED: 'Registry ghi tên miền này cho nhà đăng ký khác, không phải Tino.',
      REGISTRY_STATE_NOT_IMPORTABLE: 'Trạng thái tại registry không cho đưa vào hệ thống (đã hết hạn, đang chờ xoá, chờ chuyển hoặc chưa tạo xong).',
      TLD_NOT_CONFIGURED: 'Đuôi tên miền này chưa được thiết lập trong hệ thống.',
      REGISTRANT_NOT_MATCHED: 'Không khớp được chủ thể theo giấy tờ tại registry: hãy chọn chủ thể.',
      REGISTRANT_AMBIGUOUS: 'Nhiều chủ thể cùng số giấy tờ tại registry: hãy chọn chủ thể.',
      SUBJECT_NOT_FOUND: 'Không tìm thấy chủ thể.',
      SUBJECT_NOT_ACTIVE: 'Chủ thể không còn hoạt động.',
      SUBJECT_OF_ANOTHER_CUSTOMER: 'Chủ thể này do khách hàng khác quản lý.',
      IMPORT_ALL_RUNNING: 'Đang có một lượt đưa tất cả vào hệ thống của kết nối này. Chờ lượt đó xong.',
      NOTHING_TO_IMPORT: 'Nhà cung cấp không ghi nhận tên miền nào mà hệ thống chưa có.',
      REQUEST_IN_PROGRESS: 'Yêu cầu giống hệt vẫn đang được xử lý. Chờ một lát rồi tải lại.',
      REVEAL_DENIED: 'Bạn không được xem đầy đủ thông tin này.',
      ROUTE_NOT_FOUND: 'Chức năng này chưa có trên máy chủ đang dùng. Cập nhật hệ thống rồi thử lại.',
      SELF_APPROVAL: 'Bạn không thể tự phê duyệt yêu cầu của mình.',
      SEND_FAILED: 'Gửi không thành công. Thử lại sau.',
      SIGNING_OFF: 'Dịch vụ ký số đang tắt.',
      SOURCE_TIMEOUT: 'Nguồn dữ liệu trả lời quá chậm. Thử lại sau.',
      SOURCE_UNAVAILABLE: 'Không kết nối được nguồn dữ liệu lúc này. Thử lại sau.',
      STAFF_VERIFICATION_REMOVED: 'Nhân viên không còn tự xác minh chủ thể được: dùng eKYC hoặc tài liệu ký số.',
      SYNC_RUNNING: 'Một lần đồng bộ khác đang chạy. Chờ nó xong rồi thử lại.',
      TEST_RATE_LIMITED: 'Vừa kiểm tra xong. Đợi một lát rồi kiểm tra lại.',
      TLD_NOT_SUPPORTED: 'Hệ thống chưa hỗ trợ đuôi tên miền này.',
      TOTP_INVALID: 'Mã xác thực không đúng hoặc đã được dùng. Hãy chờ mã mới rồi nhập lại.',
      TOTP_REQUIRED: 'Cần nhập mã xác thực (TOTP).',
      UNAUTHENTICATED: 'Phiên đăng nhập đã hết. Đăng nhập lại để tiếp tục.',
      UNSUPPORTED_MEDIA_TYPE: 'Định dạng dữ liệu gửi lên không được hỗ trợ. Tải lại trang rồi thử lại.',
      VALIDATION_FAILED: 'Thông tin nhập chưa hợp lệ. Kiểm tra lại các trường rồi gửi lại.',
      VERSION_CONFLICT: 'Dữ liệu vừa được người khác thay đổi. Tải lại trang rồi thử lại.',
      VNNIC_FILE_NOT_FOUND: 'VNNIC không có tệp này.',
      VNNIC_FILE_UNAVAILABLE: 'Không lấy được tệp từ VNNIC lúc này. Thử lại sau.',
      WAITS_FOR_RUN: 'Hoá đơn điện tử này do lượt chạy 17:00 hằng ngày khởi tạo và ký; ngoài lượt chạy không gửi được. Chỉ gửi lại được hoá đơn đã lỗi.',
      WEAK_PASSWORD: 'Mật khẩu quá yếu.',
      WRITES_DISABLED: 'Máy chủ này chưa kết nối tới registry nên chưa thực hiện được thao tác.',
      ZONE_FILE_INVALID: 'Không đọc được tệp vùng: kiểm tra dòng được báo.',
      ZONE_RECORD_DATA_INVALID: 'Giá trị bản ghi chưa đúng định dạng của loại bản ghi.',
      ZONE_RECORDS_INVALID: 'Bản ghi chưa đúng quy tắc của vùng DNS.',
      ZONE_REFUSED: 'Chưa sửa được vùng DNS lúc này (vùng không hoạt động, hoặc đang gửi một thay đổi khác).',
      ZONE_TEMPLATE_INVALID: 'Không áp được mẫu: kiểm tra các giá trị mẫu yêu cầu.',
    },
    status: {
      network: 'Không kết nối được tới máy chủ. Kiểm tra mạng rồi thử lại.',
      unauthenticated: 'Phiên đăng nhập đã hết. Đăng nhập lại để tiếp tục.',
      forbidden: 'Bạn không có quyền thực hiện thao tác này.',
      notFound: 'Không tìm thấy dữ liệu yêu cầu.',
      conflict: 'Không thực hiện được ở trạng thái hiện tại. Tải lại trang để xem trạng thái mới nhất.',
      rateLimited: 'Thao tác quá dày. Đợi một lát rồi thử lại.',
      server: 'Máy chủ hoặc dịch vụ liên quan đang gặp lỗi. Thử lại sau; nếu vẫn lỗi, báo bộ phận kỹ thuật kèm mã yêu cầu.',
      invalid: 'Yêu cầu chưa hợp lệ. Kiểm tra lại thông tin rồi gửi lại.',
      other: 'Yêu cầu không thành công.',
    },
  },
  write: {
    failed: 'Yêu cầu không thành công.',
    meta: '{{code}} · Mã yêu cầu {{requestId}}',
    mfaRequired: 'Thao tác này cần phiên đăng nhập có mã xác thực.',
    mfaLink: 'Đăng nhập lại bằng TOTP',
    fourEyes: 'Cần một nhân viên khác phê duyệt trước (nguyên tắc bốn mắt). Gửi yêu cầu phê duyệt, chờ được duyệt rồi thử lại kèm ID phê duyệt.',
    selfApproval: 'Bạn là người gửi yêu cầu phê duyệt này; một nhân viên khác phải ra quyết định.',
    forbidden: 'Vai trò của bạn không có quyền cần thiết cho thao tác này.',
    keyReused: 'Biểu mẫu đã thay đổi sau lần gửi lỗi; hãy gửi lại để tạo yêu cầu mới.',
    inProgress: 'Yêu cầu giống hệt vẫn đang được xử lý. Chờ một lát rồi tải lại.',
    registryUnavailable: 'Không kết nối được registry. Chưa có gì được gửi lại; kiểm tra giao dịch registry trước khi thử lại.',
    domainProtected: 'Tên miền đang được bảo vệ: không thể yêu cầu đổi tên / chuyển nhượng trực tuyến. Liên hệ nhân viên nhà đăng ký để được xử lý thủ công.',
    registrantConsentRequired: 'Sự đồng ý của chủ thể không ghi nhận tại đây: mỗi chủ thể xác nhận bằng đường dẫn trong e-mail đã nhận.',
    notifyCustomer: 'Gửi hook cho khách',
    notifyCustomerHint: 'Bỏ chọn để không gửi webhook của thao tác này tới hệ thống của khách hàng sở hữu. Webhook của nền tảng (CRM) vẫn được gửi.',
  },
  reason: {
    title: 'Cần nêu lý do',
    intro: 'Truy vấn này nêu tên miền, cá nhân hoặc định danh cụ thể. Hãy ghi lý do (ví dụ mã sự cố hoặc mã ticket). Lý do được lưu cùng truy vấn trong nhật ký kiểm toán.',
    barIntro: 'Các truy vấn nêu đối tượng nghiệp vụ được kiểm toán kèm lý do này.',
    label: 'Lý do (tối thiểu {{min}} ký tự)',
    placeholder: 'VD: INC-2291: gia hạn example.net bị treo',
    sentAs: 'Sẽ gửi dưới dạng {{value}} (header chỉ nhận ký tự ASCII)',
    submit: 'Lưu lý do và chạy',
    current: 'Lý do truy vấn:',
    change: 'Đổi',
    clear: 'Xoá',
    none: 'Chưa đặt lý do truy vấn. Các truy vấn nêu tên miền, cá nhân hoặc ID sẽ yêu cầu nhập lý do.',
    set: 'Đặt lý do',
    notConfigured: 'Chưa khả dụng ở môi trường này',
    notConfiguredBody: '{{what}} chưa được thiết lập cho môi trường này. Các phần khác vẫn hoạt động bình thường; hãy nhờ đội vận hành nền tảng thiết lập.',
    sources: {
      metrics: 'Dữ liệu metric',
      logs: 'Tìm kiếm log',
      traces: 'Tìm kiếm trace',
      alerts: 'Cảnh báo',
      slo: 'Mục tiêu chất lượng dịch vụ (SLO)',
      other: 'Nguồn dữ liệu này',
    },
  },
  reveal: {
    hidden: '{{label}}, đang ẩn',
    sensitive: 'Rất nhạy cảm',
    sensitiveHint: 'Dữ liệu rất nhạy cảm. Mỗi lần hiển thị đều được ghi vào nhật ký kiểm toán.',
    reveal: 'Hiển thị',
    hide: 'Ẩn',
    revealedNote: 'Đã ghi nhận truy cập vào nhật ký kiểm toán. Sẽ tự động ẩn lại.',
    dialogTitle: 'Hiển thị số định danh',
    dialogBody: 'Việc hiển thị {{label}} này được ghi vào nhật ký kiểm toán kèm danh tính và lý do của bạn. Số chỉ hiện trong thời gian ngắn và không được lưu trong trình duyệt.',
    reasonLabel: 'Lý do',
    ticket: 'Mã ticket hoặc vụ việc (không bắt buộc)',
    ticketPlaceholder: 'VD: SUP-1042',
    confirm: 'Hiển thị và ghi nhật ký',
    revealing: 'Đang hiển thị…',
    reasons: {
      support: 'Yêu cầu hỗ trợ khách hàng',
      vnnic: 'Xác minh VNNIC / tuân thủ',
      legal: 'Yêu cầu pháp lý hoặc của cơ quan thực thi pháp luật',
      abuse: 'Điều tra lạm dụng hoặc gian lận',
    },
  },
  labels: {
    policyVersion: '{{name}} · phiên bản {{version}}',
    policy: {
      DOSSIER_REQUIREMENTS: 'Yêu cầu hồ sơ',
      VN_IDENTITY_VERIFICATION: 'Xác minh danh tính (.vn)',
      VN_REGISTRATION_ELIGIBILITY: 'Điều kiện đăng ký tên miền .vn',
      GTLD_REGISTRATION_ELIGIBILITY: 'Điều kiện đăng ký tên miền gTLD',
      VN_EKYC_VNNIC: 'eKYC theo VNNIC',
      VN_HSTM_CATALOG_MAPPING: 'Ánh xạ danh mục hồ sơ VNNIC (HSTM)',
      VN_SUBJECT_VERIFICATION: 'Xác minh chủ thể (.vn)',
      VN_ID_VN_REGISTRATION: 'Đăng ký tên miền .id.vn',
      VN_EXPIRATION: 'Quy định hết hạn tên miền .vn',
      VN_REGISTRATION_DATA_PUBLICATION: 'Công bố dữ liệu đăng ký tên miền .vn',
      REGISTRAR_ALL_DOMAINS_DOSSIER: 'Bắt buộc hồ sơ cho mọi tên miền',
      REGISTRAR_ALL_DOMAINS_VERIFICATION: 'Định danh chủ thể cho mọi tên miền',
      REGISTRAR_ABUSE_HANDLING: 'Xử lý lạm dụng',
      REGISTRAR_AUCTION_RULES: 'Quy tắc đấu giá',
      REGISTRAR_BACKORDER_RULES: 'Quy tắc đặt giữ tên miền',
      REGISTRAR_DATA_RETENTION: 'Lưu trữ dữ liệu',
      REGISTRAR_LOCK_EFFECTS: 'Tác động của các loại khoá',
      EXPIRY_NOTICES: 'Thông báo hết hạn',
      ICANN_GTLD_EXPIRATION: 'Hết hạn và gia hạn tên miền gTLD (ICANN)',
      ICANN_REGISTRAR_DATA_ESCROW: 'Ký quỹ dữ liệu nhà đăng ký (ICANN)',
      ICANN_REGISTRATION_DATA_DISCLOSURE: 'Cung cấp dữ liệu đăng ký (ICANN)',
      ICANN_REGISTRATION_DATA_PUBLICATION: 'Công bố dữ liệu đăng ký (ICANN)',
      ICANN_TRANSFER_POLICY: 'Chính sách chuyển đổi nhà đăng ký (ICANN)',
      ICANN_UDRP: 'Tranh chấp theo UDRP (ICANN)',
      ICANN_WHOIS_ACCURACY: 'Độ chính xác dữ liệu WHOIS (ICANN)',
    },
    workflow: {
      DOMAIN_REGISTRATION: 'Đăng ký tên miền',
      DOMAIN_RENEWAL: 'Gia hạn tên miền',
      DOMAIN_UPDATE: 'Cập nhật tên miền',
      DOMAIN_EXPIRY: 'Xử lý hết hạn',
      DOMAIN_RESTORE: 'Khôi phục tên miền',
      DOMAIN_DELETE: 'Xoá tên miền',
      DOMAIN_TRANSFER_IN: 'Chuyển đổi nhà đăng ký (chuyển đến)',
      DOMAIN_TRANSFER_OUT: 'Chuyển đổi nhà đăng ký (chuyển đi)',
      DOMAIN_REGISTRANT_CHANGE: 'Đổi chủ thể',
      VNNIC_SYNC: 'Đồng bộ hồ sơ VNNIC',
      REGISTRY_POLL: 'Nhận thông báo registry',
    },
    workflowStep: {
      AUTHORIZE: 'Kiểm tra uỷ quyền',
      DOSSIER: 'Kiểm tra hồ sơ',
      GATE: 'Kiểm tra điều kiện',
      PREPARE: 'Chuẩn bị',
      CONTACT: 'Chuẩn bị liên hệ tại registry',
      REGISTRY_CREATE: 'Tạo tại registry',
      REGISTRY_RENEW: 'Gia hạn tại registry',
      REGISTRY_RESTORE: 'Khôi phục tại registry',
      REGISTRY_UPDATE: 'Cập nhật tại registry',
      REGISTRY_REQUEST: 'Gửi yêu cầu tới registry',
      AWAIT_REGISTRY: 'Chờ registry phản hồi',
      UPDATE_REGISTRANT: 'Cập nhật chủ thể',
      CONFIRM_CURRENT_REGISTRANT: 'Chờ chủ thể hiện tại xác nhận',
      CONFIRM_NEW_REGISTRANT: 'Chờ chủ thể mới xác nhận',
      SUBJECT_VERIFY: 'Xác minh chủ thể',
      EPP_CREATE: 'Tạo tại registry',
      EPP_CREATE_RECONCILE: 'Kiểm tra lại kết quả tạo tại registry',
      EPP_RENEW: 'Gia hạn tại registry',
      EPP_TRANSFER: 'Chuyển đổi tại registry',
      EPP_UPDATE: 'Cập nhật tại registry',
      ACTIVATE: 'Kích hoạt',
      FINALIZE: 'Hoàn tất',
      COMPENSATE: 'Hoàn tác sau lỗi',
      ROLLBACK: 'Hoàn tác',
      ABANDON: 'Huỷ bỏ',
      UNHOLD: 'Giải phóng khoản tạm giữ',
      LOCK_TRANSFER: 'Khoá chuyển',
      NEW_AUTH_CODE: 'Đổi mã chuyển (Auth Code)',
    },
    event: {
      DOMAIN_REGISTRATION_REQUESTED: 'Yêu cầu đăng ký',
      DOMAIN_TRANSFER_IN_REQUESTED: 'Yêu cầu chuyển đến',
      DOMAIN_CREATED_AT_REGISTRY: 'Đã tạo tại registry',
      DOMAIN_REGISTERED: 'Đã đăng ký tên miền',
      DOMAIN_ACTIVATED: 'Tên miền đã kích hoạt',
      DOMAIN_RENEWAL_REQUESTED: 'Yêu cầu gia hạn',
      DOMAIN_RENEWED: 'Đã gia hạn tên miền',
      DOMAIN_RENEWAL_OVERRIDE: 'Nhân viên xác nhận gia hạn thêm trong 24 giờ sau lần gia hạn trước',
      DOMAIN_REGISTRY_AUTO_RENEW_OBSERVED: 'Phát hiện registry tự gia hạn',
      DOMAIN_REGISTRY_EXPIRY_OBSERVED: 'Phát hiện ngày hết hạn tại registry',
      DOMAIN_REGISTRY_STATUSES_OBSERVED: 'Phát hiện trạng thái tại registry',
      DOMAIN_REGISTRY_OUTCOME_UNKNOWN: 'Chưa rõ kết quả từ registry',
      DOMAIN_LIFECYCLE_CHANGED: 'Trạng thái vòng đời thay đổi',
      DOMAIN_EXPIRED: 'Tên miền đã hết hạn',
      DOMAIN_GRACE_PERIOD_ENTERED: 'Bắt đầu thời gian ân hạn',
      DOMAIN_REDEMPTION_ENTERED: 'Bắt đầu thời gian chuộc lại',
      DOMAIN_EXPIRY_NOTICE: 'Đã gửi thông báo hết hạn',
      DOMAIN_DELETE_REQUESTED: 'Yêu cầu xoá',
      DOMAIN_DELETED: 'Đã xoá tên miền',
      DOMAIN_RESTORED: 'Đã khôi phục tên miền',
      DOMAIN_NAMESERVERS_CHANGE_REQUESTED: 'Yêu cầu đổi máy chủ tên',
      DOMAIN_NAMESERVERS_CHANGED: 'Đã đổi máy chủ tên',
      DOMAIN_DNSSEC_CHANGED: 'Đã thay đổi DNSSEC',
      DOMAIN_AUTHINFO_CHANGED: 'Đã đổi mã AuthInfo',
      DOMAIN_REGISTRANT_CHANGE_REQUESTED: 'Yêu cầu đổi chủ thể',
      DOMAIN_REGISTRANT_CHANGE_CANCELLED: 'Đã huỷ đổi chủ thể',
      DOMAIN_REGISTRANT_CHANGE_REJECTED: 'Đổi chủ thể bị từ chối',
      DOMAIN_REGISTRANT_CHANGED: 'Đã đổi chủ thể',
      DOMAIN_CONTACTS_CHANGED: 'Đã cập nhật liên hệ',
      DOMAIN_TRANSFER_OUT_REQUESTED: 'Yêu cầu chuyển đi',
      DOMAIN_TRANSFER_OUT_REJECTED: 'Từ chối chuyển đi',
      DOMAIN_TRANSFER_OUT_CANCELLED: 'Đã huỷ chuyển đi',
      DOMAIN_TRANSFERRED_OUT: 'Đã chuyển đi',
      DOMAIN_TRANSFERRED_IN: 'Đã chuyển đến',
      DOMAIN_SUSPENDED: 'Tên miền bị tạm ngưng',
      DOMAIN_UNSUSPENDED: 'Đã bỏ tạm ngưng',
      DOMAIN_LOCK_APPLIED: 'Đã áp dụng khoá',
      DOMAIN_LOCK_RELEASED: 'Đã gỡ khoá',
      DOMAIN_DISPUTE_OPENED: 'Mở tranh chấp',
      DOMAIN_DISPUTE_DECIDED: 'Tranh chấp đã có quyết định',
      DOMAIN_DISPUTE_IMPLEMENTED: 'Đã thi hành quyết định tranh chấp',
      DOMAIN_DISPUTE_CLOSED: 'Đã đóng tranh chấp',
      DOMAIN_VNNIC_DOSSIER_ACKNOWLEDGED: 'VNNIC đã tiếp nhận hồ sơ',
      DOMAIN_VNNIC_DOSSIER_REJECTED: 'VNNIC từ chối hồ sơ',
      DOMAIN_VNNIC_DOSSIER_CORRECTION_REQUIRED: 'VNNIC yêu cầu sửa hồ sơ',
      VNNIC_SYNC_SUBMITTED: 'Đã gửi hồ sơ tới VNNIC',
      VNNIC_SYNC_ACKNOWLEDGED: 'VNNIC đã tiếp nhận hồ sơ',
      VNNIC_SYNC_REJECTED: 'VNNIC từ chối hồ sơ',
      VNNIC_SYNC_CORRECTION_REQUIRED: 'VNNIC yêu cầu sửa hồ sơ',
      VNNIC_REPORT_GENERATED: 'Đã tạo báo cáo VNNIC',
      ABUSE_DOMAIN_ACTION_REQUESTED: 'Yêu cầu xử lý do lạm dụng',
      DOSSIER_OPENED: 'Đã mở hồ sơ',
      DOSSIER_SUBJECT_ATTACHED: 'Đã thêm chủ thể vào hồ sơ',
      DOSSIER_DOCUMENT_BOUND: 'Đã thêm giấy tờ vào hồ sơ',
      DOSSIER_BOUND: 'Đã liên kết hồ sơ',
      DOSSIER_CONSENT_RECORDED: 'Đã ghi nhận sự đồng ý',
      DOSSIER_COMPLETED: 'Hồ sơ đã hoàn tất',
      DOSSIER_STATUS_CHANGED: 'Trạng thái hồ sơ thay đổi',
      DOSSIER_INVALIDATED: 'Hồ sơ bị vô hiệu',
      DOSSIER_SUPERSEDED: 'Hồ sơ được thay bằng phiên bản mới',
      REGISTRY_POLL_MESSAGE: 'Nhận thông báo từ registry',
      DOMAIN_EMAIL_SENT: 'Nhân viên gửi email cho chủ thể',
      REGISTRY_POLL_MESSAGE_RECEIVED: 'Nhận thông báo từ registry',
      CUSTOMER_CREATED: 'Đã tạo khách hàng',
      CUSTOMER_STATUS_CHANGED: 'Trạng thái khách hàng thay đổi',
      RESELLER_CREATED: 'Đã tạo đại lý',
      SUBJECT_CREATED: 'Đã tạo chủ thể',
      SUBJECT_VERSION_CREATED: 'Phiên bản chủ thể mới',
      SUBJECT_VERIFICATION_STARTED: 'Bắt đầu xác minh',
      SUBJECT_VERIFICATION_COMPLETED: 'Hoàn tất xác minh',
      SUBJECT_VERIFICATION_REVOKED: 'Thu hồi xác minh',
      SUBJECT_VERIFICATION_EXPIRED: 'Xác minh hết hiệu lực',
      ORDER_PLACED: 'Đã đặt đơn hàng',
      ORDER_ITEM_FULFILLED: 'Hạng mục đơn hàng hoàn tất',
      ORDER_ITEM_FAILED: 'Hạng mục đơn hàng thất bại',
      PAYMENT_SUCCEEDED: 'Đã nhận thanh toán',
      INVOICE_ISSUED: 'Đã xuất hoá đơn',
      CREDIT_NOTE_ISSUED: 'Đã xuất hoá đơn điều chỉnh giảm',
      DROP_SCHEDULED: 'Đã lên lịch ngày giải phóng tên miền',
      BACKORDER_PLACED: 'Đã đặt giữ tên miền',
      BACKORDER_CANCELLED: 'Đã huỷ đặt giữ',
      BACKORDER_ASSIGNED: 'Đã phân bổ lượt đặt giữ',
      BACKORDER_RETURNED: 'Đã hoàn lại lượt đặt giữ',
      BACKORDER_CAUGHT: 'Đã giành được tên miền',
      BACKORDER_NOT_CAUGHT: 'Không giành được tên miền',
      BACKORDER_WON: 'Thắng lượt đặt giữ',
      BACKORDER_LOST: 'Không thắng lượt đặt giữ',
      AUCTION_CREATED: 'Đã tạo phiên đấu giá',
      AUCTION_OPENED: 'Mở phiên đấu giá',
      AUCTION_EXTENDED: 'Kéo dài phiên đấu giá',
      AUCTION_CLOSED: 'Đóng phiên đấu giá',
      AUCTION_ENDED: 'Kết thúc phiên đấu giá',
      AUCTION_CANCELLED: 'Huỷ phiên đấu giá',
      AUCTION_UNSOLD: 'Phiên đấu giá kết thúc không có người thắng',
      BID_ACCEPTED: 'Đã nhận lượt trả giá',
      BID_REJECTED: 'Từ chối lượt trả giá',
      WINNER_SELECTED: 'Đã chọn người thắng',
      SETTLEMENT_STARTED: 'Bắt đầu tất toán',
      SETTLEMENT_FAILED: 'Tất toán thất bại',
      REGISTRATION_RESUMED: 'Đăng ký được chạy lại',
      REGISTRATION_CANCELLED: 'Nhân viên đã huỷ đăng ký',
      REGISTRATION_REACTIVATED: 'Nhân viên đã kích hoạt lại đơn đăng ký',
      REGISTRATION_STOPPED: 'Đăng ký dừng lại, chờ nhân viên xử lý',
      REGISTRY_COMMAND_REFUSED: 'Registry từ chối lệnh',
      DOMAIN_TRANSFER_IN_CANCELLED: 'Đã huỷ chuyển đến',
      DOMAIN_DELETED_BY_REGISTRY: 'Registry đã xoá tên miền',
      DOMAIN_IMPORTED: 'Đưa vào hệ thống từ registry',
      DOMAIN_PARKED: 'Đã chuyển máy chủ tên sang máy chủ chờ (hết hạn)',
      DOMAIN_UNPARKED: 'Đã trả lại máy chủ tên cũ sau khi gia hạn',
      DOMAIN_PENDING_ACTION_RESULT: 'Registry báo kết quả lệnh đang chờ',
      DOMAIN_DISPUTE_REGISTRY_LOCKED: 'Đã khoá tại registry do tranh chấp',
      DOMAIN_DISPUTE_REGISTRY_UNLOCKED: 'Đã mở khoá tại registry sau tranh chấp',
      DOSSIER_FORM_UPDATED: 'Đã cập nhật thông tin đăng ký trong hồ sơ',
      DOSSIER_REGISTRAR_ATTESTED: 'NĐK đã xác nhận bản khai',
      DOSSIER_FILE_REGISTRAR_SIGNED: 'NĐK đã ký tệp trong hồ sơ',
    },
    audit: {
      SUPPORT_ARTICLE_SAVE: 'Lưu bài hướng dẫn',
      SUPPORT_ATTACHMENT_READ: 'Xem tệp của phiếu hỗ trợ',
      SUPPORT_ATTACHMENT_UPLOAD: 'Tải tệp lên phiếu hỗ trợ',
      SUPPORT_CATEGORY_SAVE: 'Lưu nhóm bài hướng dẫn',
      SUPPORT_DEPARTMENT_SAVE: 'Lưu phòng hỗ trợ',
      SUPPORT_INBOUND_RECEIVE: 'Nhận thư hỗ trợ',
      SUPPORT_MESSAGE_ADD: 'Thêm trao đổi vào phiếu hỗ trợ',
      SUPPORT_PARTICIPANT_ADD: 'Thêm người tham gia phiếu hỗ trợ',
      SUPPORT_SLA_SIGNAL: 'Ghi nhận thời hạn hỗ trợ',
      SUPPORT_TICKET_ASSIGN: 'Phân công phiếu hỗ trợ',
      SUPPORT_TICKET_OPEN: 'Mở phiếu hỗ trợ',
      SUPPORT_TICKET_TRANSITION: 'Đổi trạng thái phiếu hỗ trợ',
      AUTH_LOGIN: 'Đăng nhập',
      APPROVAL_REQUEST: 'Gửi yêu cầu phê duyệt',
      APPROVAL_DECIDE: 'Quyết định phê duyệt',
      NOTIFY_CHANNEL_MESSAGE_RESENT: 'Gửi lại tin đến điện thoại',
      NOTIFY_CHANNEL_RESELLER_SAVED: 'Đổi việc gửi tin Zalo cho khách của đại lý',
      NOTIFY_CHANNEL_TEMPLATE_DELETED: 'Ngừng gửi thông báo qua Zalo',
      NOTIFY_CHANNEL_TEMPLATE_SAVED: 'Lưu mẫu tin Zalo',
      NOTIFY_CHANNEL_TEMPLATE_TEST_SENT: 'Gửi thử tin Zalo',
      NOTIFY_PHONE_OPT_OUT_ADDED: 'Thêm số vào danh sách không nhận tin',
      NOTIFY_PHONE_OPT_OUT_WITHDRAWN: 'Bỏ số khỏi danh sách không nhận tin',
      ROLE_GRANT: 'Cấp vai trò',
      STAFF_USER_CREATE: 'Tạo tài khoản nhân viên',
      STAFF_USER_PASSWORD_RESET: 'Đặt lại mật khẩu nhân viên',
      CUSTOMER_CREATE: 'Tạo khách hàng',
      CUSTOMER_STATUS: 'Đổi trạng thái khách hàng',
      RESELLER_CREATE: 'Tạo đại lý',
      SUBJECT_CREATE: 'Tạo chủ thể',
      SUBJECT_NEW_VERSION: 'Tạo phiên bản chủ thể mới',
      SUBJECT_VERIFICATION_COMPLETE: 'Hoàn tất xác minh',
      SUBJECT_VERIFICATION_REVOKE: 'Thu hồi xác minh',
      DOCUMENT_UPLOAD: 'Tải lên giấy tờ',
      DOCUMENT_READ: 'Xem giấy tờ',
      IDENTITY_DOCUMENT_VIEW: 'Hiển thị số định danh',
      IDENTITY_DOCUMENT_MACHINE_COMPARE: 'Kiểm tra tự động giấy tờ định danh',
      IDENTITY_DOCUMENT_REGISTRY_DISCLOSURE: 'Gửi giấy tờ định danh cho registry',
      DOSSIER_COMPLETE: 'Hoàn tất hồ sơ',
      DOMAIN_NAMESERVERS_UPDATE: 'Cập nhật máy chủ tên',
      DOMAIN_AUTHINFO_ISSUED: 'Cấp mã AuthInfo',
      DOMAIN_LOCK_APPLY: 'Áp dụng khoá',
      DOMAIN_LOCK_RELEASE: 'Gỡ khoá',
      DOMAIN_TRANSFER_LOCK_SET: 'Bật khoá chuyển nhà đăng ký',
      DOMAIN_TRANSFER_LOCK_CLEARED: 'Tắt khoá chuyển nhà đăng ký',
      DOMAIN_TRANSFER_OUT_REJECT: 'Từ chối chuyển đi',
      DOMAIN_REGISTRANT_CHANGE_REQUEST: 'Yêu cầu đổi chủ thể',
      DOMAIN_REGISTRANT_CHANGE_CONFIRMED: 'Xác nhận đổi chủ thể',
      DOMAIN_EXPIRY_CHECK: 'Kiểm tra hết hạn',
      REGISTRY_PAYLOAD_VIEW: 'Xem nội dung lệnh registry',
      BILLING_INVOICE_ISSUE: 'Xuất hoá đơn',
      BILLING_BANK_TRANSACTION_ASSIGN: 'Gán giao dịch ngân hàng cho yêu cầu thanh toán',
      BILLING_BANK_TRANSACTION_CREDIT: 'Cộng giao dịch ngân hàng vào ví',
      BILLING_BANK_TRANSACTION_DISMISS: 'Bỏ qua giao dịch ngân hàng',
      BILLING_BANK_TRANSACTION_SETTLEMENT_PAYOUT: 'Đánh dấu tiền quyết toán từ cổng thanh toán',
      BILLING_PAYMENT_CONFIRM: 'Xác nhận đã nhận chuyển khoản',
      BILLING_PAYMENT_REQUEST_CANCEL: 'Huỷ yêu cầu thanh toán',
      BILLING_PAYMENT_REQUEST_CREATE: 'Tạo yêu cầu thanh toán (mã QR)',
      BILLING_PAYMENT_SESSION_REFUND: 'Hoàn tiền về nguồn thanh toán',
      BILLING_PAYMENT_SESSION_REFUND_RESOLVE: 'Ghi nhận kết quả hoàn tiền về nguồn',
      BILLING_PAYMENT_SESSION_SYNC: 'Tra trạng thái phiên cổng thanh toán',
      BILLING_WALLET_ADJUST: 'Điều chỉnh số dư ví',
      ABUSE_CASE_CREATE: 'Tạo vụ việc lạm dụng',
      ABUSE_CASE_OPEN: 'Mở vụ việc lạm dụng',
      ABUSE_REPORT_ATTACH: 'Thêm báo cáo lạm dụng vào vụ việc',
      ABUSE_CASE_ASSIGN: 'Phân công vụ việc lạm dụng',
      ABUSE_CASE_TRANSITION: 'Đổi trạng thái vụ việc lạm dụng',
      ABUSE_EVIDENCE_ADD: 'Thêm chứng cứ',
      ABUSE_DOMAIN_ACTION: 'Xử lý tên miền do lạm dụng',
      ABUSE_DOMAIN_ACTION_REQUEST: 'Yêu cầu xử lý tên miền do lạm dụng',
      ABUSE_DOMAIN_ACTION_SETTLE: 'Hoàn tất xử lý tên miền do lạm dụng',
      ABUSE_LAW_ENFORCEMENT_RECORD: 'Ghi nhận yêu cầu của cơ quan chức năng',
      ABUSE_LAW_ENFORCEMENT_UPDATE: 'Cập nhật yêu cầu của cơ quan chức năng',
      ABUSE_LE_RECORD: 'Ghi nhận yêu cầu của cơ quan chức năng',
      ABUSE_LE_UPDATE: 'Cập nhật yêu cầu của cơ quan chức năng',
      DISPUTE_OPEN: 'Mở tranh chấp',
      DISPUTE_DECIDE: 'Quyết định tranh chấp',
      DISPUTE_IMPLEMENT: 'Thi hành quyết định tranh chấp',
      DISPUTE_CLOSE: 'Đóng tranh chấp',
      DISCLOSURE_CASE_OPENED: 'Mở yêu cầu cung cấp dữ liệu',
      DISCLOSURE_REVIEW_STARTED: 'Bắt đầu xem xét yêu cầu cung cấp dữ liệu',
      DISCLOSURE_INFO_REQUESTED: 'Yêu cầu bổ sung thông tin',
      DISCLOSURE_DECIDED: 'Quyết định cung cấp dữ liệu',
      DISCLOSURE_RELEASED: 'Đã cung cấp dữ liệu đăng ký',
      DISCLOSURE_CLOSED: 'Đóng yêu cầu cung cấp dữ liệu',
      AUCTION_CREATE: 'Tạo phiên đấu giá',
      AUCTION_CANCEL: 'Huỷ phiên đấu giá',
      AUCTION_DROP_SCHEDULE: 'Lên lịch ngày giải phóng tên miền',
      AUCTION_RESULT_OVERRIDE: 'Ghi đè kết quả đấu giá',
      ESCROW_DEPOSIT_CREATED: 'Bắt đầu tạo bản ký quỹ',
      ESCROW_DEPOSIT_GENERATED: 'Đã tạo bản ký quỹ',
      ESCROW_DEPOSIT_VALIDATED: 'Bản ký quỹ hợp lệ',
      ESCROW_DEPOSIT_VALIDATION_FAILED: 'Bản ký quỹ không hợp lệ',
      ESCROW_DEPOSIT_ENCRYPTED: 'Đã mã hoá bản ký quỹ',
      ESCROW_DEPOSIT_DELIVERY_STARTED: 'Bắt đầu gửi bản ký quỹ',
      ESCROW_DEPOSIT_DELIVERED: 'Đã gửi bản ký quỹ',
      ESCROW_DEPOSIT_DELIVERY_FAILED: 'Gửi bản ký quỹ thất bại',
      ESCROW_DEPOSIT_DELIVERY_UNKNOWN: 'Chưa rõ kết quả gửi bản ký quỹ',
      ESCROW_DEPOSIT_CONFIRMED: 'Bên nhận ký quỹ đã xác nhận',
      ESCROW_DEPOSIT_VERIFICATION_FAILED: 'Bên nhận ký quỹ không xác minh được bản ký quỹ',
      EINVOICE_DOCUMENT_CHANGE: 'Thực hiện điều chỉnh, thay thế, huỷ hoặc bỏ hoá đơn điện tử',
      EINVOICE_DOCUMENT_DOWNLOAD: 'Tải tệp hoá đơn điện tử',
      EINVOICE_DOCUMENT_ISSUE: 'Phát hành, gửi lại hoặc tra cứu hoá đơn điện tử',
      ESCROW_DEPOSIT_ABANDONED: 'Đã bỏ bản ký quỹ',
      ESCROW_RECEIPT_RECORDED: 'Ghi nhận biên nhận ký quỹ',
      VNNIC_SYNC_SUBMIT: 'Gửi hồ sơ tới VNNIC',
      VNNIC_SYNC_OUTCOME: 'Ghi nhận kết quả đồng bộ VNNIC',
      VNNIC_SYNC_OUTCOME_RECORDED: 'Ghi nhận kết quả đồng bộ VNNIC',
      VNNIC_SYNC_RESUBMIT: 'Gửi lại hồ sơ tới VNNIC',
      VNNIC_SYNC_RESUBMIT_REQUESTED: 'Yêu cầu gửi lại hồ sơ tới VNNIC',
      VNNIC_REPORT_GENERATE: 'Tạo báo cáo VNNIC',
      VNNIC_REPORT_GENERATED: 'Tạo báo cáo VNNIC',
      VNNIC_REPORT_EXPORT: 'Xuất báo cáo VNNIC',
      VNNIC_REPORT_EXPORTED: 'Xuất báo cáo VNNIC',
      VNNIC_REPORT_OUTCOME: 'Ghi nhận kết quả báo cáo VNNIC',
      VNNIC_REPORT_OUTCOME_RECORDED: 'Ghi nhận kết quả báo cáo VNNIC',
      VNNIC_REGISTRY_ENTERPRISE_LOOKUP: 'Tra cứu doanh nghiệp qua VNNIC',
      VNNIC_REGISTRY_BUSINESS_HOUSEHOLD_LOOKUP: 'Tra cứu hộ kinh doanh qua VNNIC',
      VNNIC_REGISTRY_VERIFICATION_LOOKUP: 'Tra cứu kết quả xác minh qua VNNIC',
      VNNIC_REGISTRY_PROMOTION_CODE: 'Yêu cầu mã khuyến mại VNNIC',
      INTEGRATION_CONNECTION_CREATE: 'Tạo kết nối registry',
      INTEGRATION_CONNECTION_UPDATE: 'Cập nhật kết nối registry',
      INTEGRATION_CONNECTION_TEST: 'Kiểm tra kết nối',
      INTEGRATION_ENVIRONMENT_PROPOSE: 'Đề xuất cấu hình kết nối',
      INTEGRATION_ENVIRONMENT_ACTIVATE: 'Kích hoạt cấu hình kết nối',
      INTEGRATION_CREDENTIAL_PROPOSE: 'Đề xuất thông tin đăng nhập',
      INTEGRATION_CREDENTIAL_ACTIVATE: 'Kích hoạt thông tin đăng nhập',
      INTEGRATION_CREDENTIAL_REVOKE: 'Thu hồi thông tin đăng nhập',
      INTEGRATION_CERTIFICATE_PROPOSE: 'Đề xuất chứng thư',
      INTEGRATION_CERTIFICATE_ACTIVATE: 'Kích hoạt chứng thư',
      INTEGRATION_CERTIFICATE_REVOKE: 'Thu hồi chứng thư',
      INTEGRATION_SECRET_STORE: 'Lưu thông tin bí mật',
      INTEGRATION_SECRET_DESTROY: 'Huỷ thông tin bí mật',
      INTEGRATION_TLD_CHANNEL_PROPOSE: 'Đề xuất đổi kênh đăng ký TLD',
      INTEGRATION_TLD_CHANNEL_ACTIVATE: 'Kích hoạt kênh đăng ký TLD',
      INTEGRATION_PROVIDER_SETTING_PROPOSE: 'Đề xuất cấu hình nhà cung cấp',
      INTEGRATION_PROVIDER_SETTING_ACTIVATE: 'Kích hoạt cấu hình nhà cung cấp',
      INTEGRATION_MODULE_INSTANCE_ACTION: 'Chạy thao tác của module',
      INTEGRATION_MODULE_INSTANCE_ACTIVATE: 'Áp dụng cài đặt module',
      INTEGRATION_MODULE_INSTANCE_CREATE: 'Thêm kết nối module',
      INTEGRATION_MODULE_INSTANCE_DISABLE: 'Tắt kết nối module',
      INTEGRATION_MODULE_INSTANCE_DRAFT: 'Lưu bản nháp cài đặt module',
      INTEGRATION_MODULE_INSTANCE_ENABLE: 'Bật kết nối module',
      INTEGRATION_MODULE_INSTANCE_PROPOSE: 'Gửi duyệt cài đặt module',
      INTEGRATION_MODULE_INSTANCE_TEST: 'Kiểm tra kết nối module',
      ADDON_INSTALL: 'Cài đặt tiện ích',
      ADDON_ACTIVATE: 'Bật tiện ích',
      ADDON_DEACTIVATE: 'Tắt tiện ích',
      ADDON_UNINSTALL: 'Gỡ tiện ích',
      ADDON_CIRCUIT_OPEN: 'Tiện ích tự dừng vì lỗi liên tục',
      ADDON_EVENT_REPLAY: 'Gửi lại sự kiện cho tiện ích',
      ADDON_ACTION: 'Thao tác trên trang của tiện ích',
      INTEGRATION_SPONSORING_REGISTRAR_PROPOSE: 'Đề xuất nhà đăng ký bảo trợ',
      INTEGRATION_SPONSORING_REGISTRAR_ACTIVATE: 'Kích hoạt nhà đăng ký bảo trợ',
      INTEGRATION_CHANGE_APPLY: 'Áp dụng thay đổi',
      INTEGRATION_CHANGE_CANCEL: 'Huỷ thay đổi',
      OPS_INCIDENT_OPEN: 'Mở sự cố',
      OPS_INCIDENT_OPENED: 'Mở sự cố',
      OPS_INCIDENT_TRANSITION: 'Đổi trạng thái sự cố',
      AUTH_LOGOUT: 'Đăng xuất',
      ACCESS_CHANGE_PROPOSE: 'Đề xuất thay đổi quyền truy cập',
      ACCESS_CHANGE_APPLY: 'Áp dụng thay đổi quyền truy cập',
      ACCESS_CHANGE_CANCEL: 'Huỷ thay đổi quyền truy cập',
      API_CREDENTIAL_ISSUE: 'Cấp khoá API',
      API_CREDENTIAL_REVOKE: 'Thu hồi khoá API',
      CUSTOMER_CONTACT_SHARING: 'Đổi việc gửi thông tin liên hệ cho hệ thống quản lý khách hàng',
      API_CREDENTIAL_EXPIRY: 'Rút ngắn hạn của khoá API',
      REPORTING_CONSUMER_CREATE: 'Thêm nơi nhận dữ liệu',
      REPORTING_CONSUMER_UPDATE: 'Đổi tên nơi nhận dữ liệu',
      REPORTING_CONSUMER_CHANGE: 'Đổi dữ liệu nơi nhận được nhận',
      REPORTING_CHECKPOINT_RESET: 'Đồng bộ lại dữ liệu báo cáo từ đầu',
      REPORTING_KEY_ROTATE: 'Đổi khoá API báo cáo',
      REPORTING_KEY_SUSPEND: 'Tạm khoá khoá API báo cáo',
      REPORTING_KEY_RESUME: 'Mở lại khoá API báo cáo',
      REPORTING_KEY_REVOKE: 'Thu hồi khoá API báo cáo',
      REPORTING_PARKED_RESEND: 'Gửi lại dòng chưa nhận được',
      WEBHOOK_REPORTING_ENDPOINT_SAVE: 'Lưu địa chỉ nhận tín hiệu thay đổi',
      WEBHOOK_REPORTING_ENDPOINT_DISABLE: 'Gỡ địa chỉ nhận tín hiệu thay đổi',
      APPROVAL_SELF_APPROVE: 'Tự phê duyệt',
      ROLE_REVOKE: 'Thu hồi vai trò',
      STAFF_USER_DISABLE: 'Vô hiệu hoá tài khoản nhân viên',
      STAFF_USER_ENABLE: 'Kích hoạt lại tài khoản nhân viên',
      STAFF_USER_MFA_RESET: 'Đặt lại xác thực hai lớp của nhân viên',
      STAFF_USER_UNLOCK: 'Mở khoá tài khoản nhân viên',
      CUSTOMER_UPDATE: 'Sửa thông tin khách hàng',
      CUSTOMER_USER_ADD: 'Thêm người dùng cho khách hàng',
      CUSTOMER_USER_CREATE: 'Tạo người dùng khách hàng',
      AUCTION_OPEN: 'Mở phiên đấu giá',
      AUCTION_CLOSE: 'Đóng phiên đấu giá',
      BACKORDER_STAFF_CANCEL: 'Nhân viên huỷ đặt giữ tên miền',
      COMPLIANCE_WORK_ITEM_OPEN: 'Mở việc cần xử lý',
      COMPLIANCE_WORK_ITEM_ASSIGN: 'Phân công việc cần xử lý',
      COMPLIANCE_WORK_ITEM_RESOLVE: 'Hoàn tất việc cần xử lý',
      COMPLIANCE_WORK_ITEM_AUTO_RESOLVE: 'Tự động hoàn tất việc cần xử lý',
      DISPUTE_REGISTRY_LOCK: 'Khoá tại registry do tranh chấp',
      DISPUTE_REGISTRY_UNLOCK: 'Gỡ khoá tại registry sau tranh chấp',
      DNS_LOGIN_RECORDS_EDIT: 'Sửa bản ghi DNS qua đăng nhập DNS của tên miền',
      DNS_LOGIN_SIGN_IN: 'Đăng nhập DNS của tên miền',
      DNS_ZONE_IMPORT: 'Nhập vùng DNS từ tệp vùng',
      DNS_ZONE_LOGIN_REVOKE: 'Thu hồi đăng nhập DNS của tên miền',
      DNS_ZONE_LOGIN_SET: 'Đặt mật khẩu đăng nhập DNS của tên miền',
      DNS_ZONE_RECORDS_EDIT: 'Nhân viên sửa bản ghi DNS',
      DNS_ZONE_ROLLBACK: 'Quay vùng DNS về phiên bản cũ',
      DNS_ZONE_TEMPLATE_APPLY: 'Áp mẫu vùng DNS',
      DNS_ZONE_TSIG_KEY_SET: 'Đặt khoá bảo vệ việc sao chép vùng DNS (TSIG)',
      DOMAIN_STAFF_REGISTER: 'Nhân viên đăng ký tên miền cho khách hàng',
      DOMAIN_STAFF_AUTH_INFO: 'Nhân viên tạo mã chuyển',
      DOMAIN_STAFF_DELETE: 'Nhân viên xoá tên miền',
      DOMAIN_STAFF_DNSSEC: 'Nhân viên cập nhật DNSSEC',
      DOMAIN_STAFF_NAMESERVERS: 'Nhân viên đổi máy chủ tên',
      DOMAIN_STAFF_REGISTRANT_CHANGE: 'Nhân viên yêu cầu đổi chủ thể',
      DOMAIN_STAFF_REGISTRANT_CHANGE_CONFIRM: 'Nhân viên xác nhận đổi chủ thể',
      DOMAIN_STAFF_RENEW: 'Nhân viên gia hạn tên miền',
      DOMAIN_STAFF_RENEWAL_OVERRIDE: 'Nhân viên xác nhận gia hạn thêm trong 24 giờ',
      DOMAIN_STAFF_TRANSFER_CANCEL: 'Nhân viên huỷ chuyển nhà đăng ký',
      DOMAIN_STAFF_TRANSFER_LOCK: 'Nhân viên đổi khoá chuyển nhà đăng ký',
      DOMAIN_STAFF_UPDATE_LOCK: 'Nhân viên đổi khoá cập nhật',
      DOMAIN_STAFF_CLIENT_HOLD: 'Nhân viên khoá / mở khoá tên miền (clientHold)',
      DOMAIN_TRANSFER_IN_CANCEL: 'Huỷ chuyển tên miền về',
      ESCROW_DEPOSIT_OBJECTS_PURGED: 'Xoá tệp ký quỹ hết hạn lưu giữ',
      INTEGRATION_OTE_EXERCISE_START: 'Bắt đầu bài chạy OT&E',
      INTEGRATION_OTE_EXERCISE_FINISH: 'Kết thúc bài chạy OT&E',
      INTEGRATION_BALANCE_READ: 'Đọc số dư tại registry',
      INTEGRATION_ACCOUNT_DOMAINS_READ: 'Đọc danh sách tên miền tại registry',
      INTEGRATION_CREDENTIAL: 'Thao tác thông tin đăng nhập registry',
      INTEGRATION_CERTIFICATE: 'Thao tác chứng thư registry',
      INTEGRATION_MANAGE: 'Quản lý tích hợp',
      NOTIFY_NOTIFICATION_RESENT: 'Gửi lại thông báo',
      NOTIFY_SETTINGS_SAVED: 'Lưu cấu hình thông báo',
      NOTIFY_SETTINGS_TEST: 'Gửi thử thông báo',
      NOTIFY_TEMPLATE_SAVED: 'Lưu mẫu thông báo',
      NOTIFY_TEMPLATE_REVERTED: 'Khôi phục mẫu thông báo mặc định',
      PRICING_BOOK_CREATE: 'Tạo bảng giá',
      PRICING_BOOK_STATUS: 'Đổi trạng thái bảng giá',
      PRICING_PROMOTION_CREATE: 'Tạo khuyến mại',
      PRICING_PROMOTION_UPDATE: 'Sửa khuyến mại',
      PRICING_PROMOTION_DISABLE: 'Tắt khuyến mại',
      PRICING_PROMOTION_SCOPE: 'Đặt phạm vi khuyến mại theo nhóm, sản phẩm hoặc gói',
      CATALOG_GROUP_SAVE: 'Lưu nhóm sản phẩm',
      CATALOG_PRODUCT_SAVE: 'Lưu sản phẩm',
      CATALOG_PRODUCT_VISIBILITY: 'Hiện, ẩn hoặc ngừng sản phẩm',
      CATALOG_PLAN_SAVE: 'Lưu gói',
      CATALOG_PLAN_PUBLISH: 'Mở bán hoặc ngừng bán gói',
      CATALOG_FULFILMENT_SET: 'Đặt cách cấp phát của gói',
      CATALOG_STOCK_SET: 'Đặt tồn kho của gói',
      SUBSCRIPTION_CANCEL_DELIVERY: 'Không thể cấp phát dịch vụ thủ công',
      SUBSCRIPTION_COMPLETE_DELIVERY: 'Xác nhận đã cấp phát dịch vụ thủ công',
      SUBSCRIPTION_EXTEND: 'Cộng thời hạn dịch vụ',
      SUBSCRIPTION_ORDER: 'Tạo dịch vụ mới cho khách',
      SUBSCRIPTION_SUSPEND: 'Tạm ngưng dịch vụ',
      SUBSCRIPTION_TERMINATE: 'Yêu cầu huỷ dịch vụ',
      SUBSCRIPTION_UNSUSPEND: 'Mở lại dịch vụ',
      HOSTING_ACCOUNT_AUTOSSL: 'Chạy AutoSSL cho tài khoản hosting',
      HOSTING_ACCOUNT_PASSWORD_SET: 'Đặt mật khẩu cPanel',
      HOSTING_ACCOUNT_REFRESH: 'Đồng bộ tài khoản hosting từ máy chủ',
      HOSTING_ACCOUNT_SIGN_IN: 'Đăng nhập cPanel thay khách',
      HOSTING_OPERATION_RESOLVE: 'Xử lý thao tác cấp phát chưa rõ kết quả',
      HOSTING_SERVER_ACCEPTING: 'Bật hoặc tắt nhận tài khoản mới trên máy chủ hosting',
      HOSTING_SERVER_REFRESH: 'Đọc lại danh sách máy chủ hosting',
      CATALOG_CROSS_SELLS_SET: 'Đặt sản phẩm bán kèm',
      CATALOG_FREE_DOMAINS_SET: 'Đặt tên miền tặng kèm gói',
      CATALOG_OPTION_SAVE: 'Lưu tuỳ chọn cấu hình',
      CATALOG_ADDON_SAVE: 'Lưu add-on',
      CATALOG_LOCATION_SAVE: 'Lưu vị trí (trung tâm dữ liệu)',
      CATALOG_PLACEMENT_GROUP_SAVE: 'Lưu nhóm máy chủ',
      CATALOG_LIFECYCLE_SET: 'Đặt cài đặt quá hạn (gia hạn, tạm ngưng, huỷ)',
      CATALOG_PLAN_PRICE_PUBLISH: 'Công bố giá gói theo chu kỳ',
      CATALOG_PLAN_PRICE_END: 'Kết thúc giá gói',
      CATALOG_OPTION_PRICE_PUBLISH: 'Công bố giá tuỳ chọn cấu hình',
      CATALOG_ADDON_PRICE_PUBLISH: 'Công bố giá add-on',
      CATALOG_PRICE_RULE_PUBLISH: 'Công bố chiết khấu giá theo hạng hoặc giá riêng',
      CATALOG_PRICE_RULE_END: 'Kết thúc chiết khấu giá theo hạng hoặc giá riêng',
      PRICING_REGISTRY_PRICE_PUBLISH: 'Công bố giá registry',
      PRICING_REGISTRY_PRICE_END: 'Kết thúc giá registry',
      PRICING_RULE_PUBLISH: 'Công bố quy tắc giá',
      PRICING_TAX_DEFAULT_SET: 'Đặt mã thuế mặc định',
      PRICING_TAX_RATE_END: 'Kết thúc thuế suất',
      PRICING_TAX_RATE_PUBLISH: 'Công bố thuế suất',
      PRICING_RULE_END: 'Kết thúc quy tắc giá',
      REFERENCE_VN_PLACES_SYNC: 'Đồng bộ danh mục tỉnh/phường',
      REGISTRY_POLL_ACK: 'Xác nhận thư poll registry',
      REGISTRY_POLL_PROCESSED: 'Xử lý thư poll registry',
      REGISTRY_RECONCILIATION_RESOLVE: 'Xử lý mục đối soát',
      WEBHOOK_DELIVERY_RESEND: 'Gửi lại webhook',
      WEBHOOK_ENDPOINT_SAVE: 'Lưu điểm nhận webhook',
      WEBHOOK_ENDPOINT_TEST: 'Gửi thử webhook',
      VNNIC_REGISTRY_VERIFICATION_LOOKUP_DENIED: 'Tra cứu xác minh qua VNNIC bị từ chối',
      OPS_INCIDENT_ACKNOWLEDGE: 'Tiếp nhận sự cố',
      ADMIN_USERS_MANAGE: 'Quản lý nhân viên',
      AUDIT_READ: 'Xem nhật ký kiểm toán',
      COMPLIANCE_READ: 'Xem dữ liệu tuân thủ',
      DOSSIER_READ: 'Xem hồ sơ',
      ACCOUNT_USER_INVITE_ACCEPT: 'Nhận lời mời vào tài khoản',
      AUTH_ACCOUNT_SWITCH: 'Chuyển tài khoản đang làm việc',
      AUTH_CAPTCHA_FAILED: 'Không qua bước xác minh không phải robot',
      AUTH_DEMO_LOGIN: 'Đăng nhập nhanh (OT&E)',
      AUTH_PASSWORD_RESET: 'Đặt lại mật khẩu',
      CLIENT_USER_EMAIL_VERIFY: 'Xác minh e-mail người dùng khách',
      CLIENT_USER_SIGN_UP: 'Người dùng khách đăng ký',
      COMPLIANCE_POST_OPERATION_BACKFILL: 'Chạy bù bước sau nghiệp vụ',
      COMPLIANCE_REGISTRAR_SIGNATURE_SETTINGS: 'Đổi cài đặt ký của nhà đăng ký',
      CUSTOMER_IDENTITY_CARRIED: 'Mang thông tin định danh sang khách hàng',
      CUSTOMER_PRIMARY_SUBJECT_SET: 'Đặt chủ thể chính của khách hàng',
      DECLARATION_CHECK_SETTINGS_UPDATE: 'Đổi cài đặt kiểm tra bản khai',
      DECLARATION_GENERATE: 'Tạo bản khai',
      DECLARATION_REFUSED: 'Từ chối bản khai',
      DECLARATION_UPLOAD: 'Nộp bản khai',
      DECLARATION_UPLOAD_BY_STAFF: 'Nhân viên tải bản khai lên',
      DOCUMENT_CONTENT: 'Xem nội dung tài liệu',
      DOMAIN_ADDON_APPLIED: 'Áp dụng dịch vụ thêm',
      DOMAIN_ADDON_CANCELLED: 'Huỷ dịch vụ thêm',
      DOMAIN_ADDON_ENDED: 'Kết thúc dịch vụ thêm',
      DOMAIN_ADDON_ORDERED: 'Đặt dịch vụ thêm',
      DOMAIN_ADDON_UNLOCK_REQUESTED: 'Yêu cầu mở khoá dịch vụ thêm',
      DOMAIN_EXTRA_FIELDS_EDIT: 'Sửa thông tin bổ sung của tên miền',
      DOMAIN_FULL_LOCK_APPLIED: 'Áp dụng khoá toàn phần',
      DOMAIN_CLIENT_STATUSES_CHANGED: 'Đổi trạng thái bảo vệ tại registry',
      DOMAIN_NAME_CHANGE_CANCELLED: 'Huỷ yêu cầu đổi tên',
      DOMAIN_NAME_CHANGE_COMPLETED: 'Hoàn tất đổi tên',
      DOMAIN_NAME_CHANGE_CONSENT: 'Ghi nhận đồng ý đổi tên',
      DOMAIN_NAME_CHANGE_DECLARATION: 'Nộp bản khai đổi tên',
      DOMAIN_NAME_CHANGE_OPENED: 'Mở yêu cầu đổi tên',
      DOMAIN_NAME_CHANGE_REVIEWED: 'Duyệt yêu cầu đổi tên',
      DOMAIN_NAME_CHANGE_SUBMITTED: 'Gửi yêu cầu đổi tên',
      DOMAIN_NAME_CHANGE_VNNIC_REFUSED: 'VNNIC từ chối đổi tên',
      DOMAIN_PANEL_OPERATION_COMPLETED: 'Hoàn tất thao tác trên hệ thống VNNIC',
      DOMAIN_PANEL_OPERATION_OPEN: 'Mở thao tác trên hệ thống VNNIC',
      DOMAIN_PANEL_OPERATION_SUBMITTED: 'Ghi nhận đã nhập trên hệ thống VNNIC',
      DOMAIN_PROTECTED_CHANGE_PENDING: 'Thay đổi tên miền bảo vệ chờ xác nhận',
      DOMAIN_STAFF_NAME_CHANGE_CONSENT: 'Nhân viên ghi nhận đồng ý đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_DECLARATION: 'Nhân viên nộp bản khai đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_DOCUMENT: 'Nhân viên tải tài liệu đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_OPEN: 'Nhân viên mở yêu cầu đổi tên',
      DOMAIN_STAFF_PANEL_SUBMITTED: 'Nhân viên ghi nhận đã nhập trên hệ thống VNNIC',
      DOMAIN_STAFF_RESTORE: 'Nhân viên khôi phục tên miền',
      DOMAIN_STAFF_TRANSFER_IN: 'Nhân viên đặt chuyển tên miền về',
      DOMAIN_UPSTREAM_VERIFICATION_RESENT: 'Gửi lại e-mail xác minh của nhà đăng ký phía trên',
      DOSSIER_CASE: 'Ghi nhận nghiệp vụ của hồ sơ',
      DOSSIER_CASE_BY_STAFF: 'Nhân viên ghi nhận nghiệp vụ của hồ sơ',
      DOSSIER_DOCUMENT_RECLASSIFIED: 'Đổi loại tài liệu trong hồ sơ',
      DOSSIER_DOCUMENT_TYPE_UPDATE: 'Sửa loại hồ sơ',
      DOSSIER_FILE_UPLOAD: 'Tải tệp lên hồ sơ',
      DOSSIER_FILE_UPLOAD_BY_STAFF: 'Nhân viên tải tệp lên hồ sơ',
      DOSSIER_NOTE_ADD: 'Thêm ghi chú hồ sơ',
      DOSSIER_REGISTRAR_ATTESTED: 'Nhà đăng ký xác nhận hồ sơ',
      DOSSIER_REGISTRAR_SIGNED_FILE: 'Nhà đăng ký ký tệp hồ sơ',
      DOSSIER_REMINDER_SEND: 'Nhắc khách bổ sung hồ sơ',
      MFA_ENROL: 'Bật xác thực hai lớp',
      MFA_RESET: 'Đặt lại xác thực hai lớp',
      NAME_CHANGE_REQUIREMENTS_PUBLISH: 'Ban hành bộ yêu cầu đổi tên',
      REGISTRY_TLD_DEFAULT_CONNECTION: 'Đổi kết nối mặc định của TLD',
      REGISTRY_TLD_DISABLE: 'Tắt TLD',
      REGISTRY_TLD_ENABLE: 'Bật TLD',
      REGISTRY_TLD_PUBLICATION: 'Đổi cách công bố dữ liệu của TLD',
      REGISTRY_TLD_SETUP: 'Thiết lập TLD',
      SUBJECT_DECLARATION_CARRIED: 'Mang bản khai sang chủ thể',
      SUBJECT_LEGAL_REPRESENTATIVE_SET: 'Đặt người đại diện theo pháp luật',
      VNNIC_DOMAIN_TRACKING_REPORT_SEND: 'Gửi báo cáo theo dõi tên miền hằng ngày',
      VNNIC_DOSSIER_FILE_READ: 'Xem tệp hồ sơ trên VNNIC',
      VNNIC_PROMO_CODE_REJECTED: 'VNNIC từ chối mã khuyến mại',
      VNNIC_PROMO_CODE_REQUEST: 'Xin mã khuyến mại VNNIC',
      VNNIC_PROMO_CODE_RESERVE: 'Giữ mã khuyến mại VNNIC',
      VNNIC_PROMO_CODE_USED: 'Dùng mã khuyến mại VNNIC',
      VNNIC_REGISTRY_LIST_CHECK: 'Đối chiếu danh sách tên miền VNNIC',
      COMPLIANCE_REGISTRAR_SIGNATURE_DECLARATION_ACCEPTED: 'Nhận bản khai vào hàng chờ NĐK ký',
      ACCOUNT_USER_INVITE: 'Mời người dùng vào tài khoản',
      COMPLIANCE_REGISTRAR_SIGNATURE_DECLARATION_BACKFILLED: 'Chạy bù bản khai vào hàng chờ NĐK ký',
      COMPLIANCE_REGISTRAR_SIGNATURE_AUTO_ITEM_SATISFIED: 'Mục chờ NĐK ký đã được đáp ứng',
      COMPLIANCE_REGISTRAR_SIGNATURE_FILE_REQUESTED: 'Yêu cầu NĐK ký tệp',
      COMPLIANCE_REGISTRAR_SIGNATURE_ITEM_CANCELLED: 'Huỷ yêu cầu NĐK ký (tên miền không được tạo)',
      COMPLIANCE_REGISTRAR_SIGNATURE_BATCH_OPENED: 'Mở lô ký của NĐK',
      COMPLIANCE_REGISTRAR_SIGNATURE_BATCH_SENT: 'Gửi lô ký của NĐK',
      AUCTION_BID: 'Trả giá đấu giá',
      AUTH_LOGIN_FAILED: 'Đăng nhập không thành công',
      AUTH_STEP_UP: 'Xác thực lại bằng mã',
      BACKORDER_CANCEL: 'Huỷ đặt giữ tên miền',
      BACKORDER_CREATE: 'Đặt giữ tên miền',
      DOMAIN_CONTACTS_CHANGED: 'Cập nhật liên hệ của tên miền',
      DOMAIN_DELETE_REQUEST: 'Yêu cầu xoá tên miền',
      DOMAIN_DNSSEC_CHANGE: 'Đổi DNSSEC',
      DOMAIN_NAMESERVERS_CHANGE: 'Đổi máy chủ tên',
      DOMAIN_PARKING_PARKED: 'Chuyển sang máy chủ tên tạm khi hết hạn',
      DOMAIN_PARKING_UNPARKED: 'Trả lại máy chủ tên cũ sau khi gia hạn',
      DOMAIN_PROTECTED_CHANGE_CONFIRMED: 'Xác nhận thay đổi trên tên miền được bảo vệ',
      DOMAIN_PROTECTED_CHANGE_REJECTED: 'Từ chối thay đổi trên tên miền được bảo vệ',
      DOMAIN_PROTECTION_REMOVED: 'Tắt bảo vệ tên miền',
      DOMAIN_PROTECTION_SET: 'Bật bảo vệ tên miền',
      DOMAIN_REGISTER: 'Đăng ký tên miền',
      DOMAIN_REGISTRATION_CANCEL: 'Nhân viên huỷ đăng ký',
      DOMAIN_REGISTRATION_REACTIVATE: 'Nhân viên kích hoạt lại đơn đăng ký',
      DOMAIN_REGISTRATION_RETRY: 'Nhân viên chạy lại đăng ký',
      DOMAIN_RENEW: 'Gia hạn tên miền',
      DOMAIN_RESTORE: 'Khôi phục tên miền',
      DOMAIN_STAFF_ADDON_APPLY: 'Nhân viên ghi nhận đã thực hiện dịch vụ kèm theo',
      DOMAIN_STAFF_ADDON_CANCEL: 'Nhân viên huỷ dịch vụ kèm theo',
      DOMAIN_STAFF_ADDON_ORDER: 'Nhân viên đặt dịch vụ kèm theo',
      DOMAIN_STAFF_NAME_CHANGE_CANCEL: 'Nhân viên huỷ yêu cầu đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_CONFIRM_COMPLETION: 'Nhân viên xác nhận hoàn tất đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_REVIEW: 'Nhân viên xét duyệt yêu cầu đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_SUBMIT: 'Nhân viên gửi yêu cầu đổi tên',
      DOMAIN_STAFF_NAME_CHANGE_VNNIC_REFUSED: 'Nhân viên ghi nhận VNNIC từ chối đổi tên',
      DOMAIN_STAFF_PANEL_CHECK: 'Nhân viên kiểm tra thao tác trên hệ thống VNNIC',
      DOMAIN_STAFF_PANEL_REFUSED: 'Nhân viên ghi nhận registry từ chối khoá / mở khoá',
      DOMAIN_STAFF_TRANSFER_OUT_RESPOND: 'Nhân viên trả lời yêu cầu chuyển đi',
      DOMAIN_STAFF_UPSTREAM_REFRESH: 'Nhân viên đọc lại trạng thái từ nhà đăng ký cấp trên',
      DOMAIN_STAFF_REGISTRY_SYNC: 'Nhân viên đồng bộ tên miền với registry',
      DOMAIN_STAFF_IMPORT: 'Nhân viên đưa tên miền vào hệ thống từ registry',
      DOMAIN_STAFF_IMPORT_ALL: 'Nhân viên đưa tất cả tên miền của nhà cung cấp vào hệ thống',
      SUBJECT_CUSTOMER_ASSIGN: 'Gắn chủ thể vào khách hàng',
      DOMAIN_STAFF_REGISTRY_PREVIEW: 'Nhân viên xem tên miền tại registry',
      DOMAIN_STAFF_VERIFICATION_RESENT: 'Nhân viên gửi lại email xác minh',
      DOMAIN_STAFF_EMAIL_SENT: 'Nhân viên gửi email theo mẫu',
      DOMAIN_STAFF_REGISTRANT_CONTACT: 'Nhân viên tạo registrant mới tại registry',
      DOMAIN_STAFF_REGISTRY_CONTACTS: 'Nhân viên xem liên hệ tại registry',
      DOMAIN_TRANSFER_IN_START: 'Bắt đầu chuyển tên miền về',
      DOMAIN_TRANSFER_OUT_RESPOND: 'Trả lời yêu cầu chuyển đi',
      INTEGRATION_CERTIFICATE_IMPORT_PKCS12: 'Nhập chứng thư từ tệp .p12',
      INTEGRATION_CONNECTION_ROLE: 'Đổi vai trò nhà đăng ký của kết nối',
      OPS_INCIDENT_ACKNOWLEDGED: 'Tiếp nhận sự cố',
      OPS_INCIDENT_MITIGATING: 'Bắt đầu khắc phục sự cố',
      OPS_INCIDENT_MONITORING: 'Theo dõi sự cố sau khắc phục',
      OPS_INCIDENT_RESOLVED: 'Đã xử lý sự cố',
      OPS_LOG_RETENTION_PURGE: 'Xoá log quá hạn khỏi kho log',
      OPS_LOG_RETENTION_SETTINGS: 'Đổi thời gian lưu log',
      PRICING_ADDON_CLEANUP: 'Chuẩn hoá dữ liệu dịch vụ kèm theo',
      PRICING_ADDON_CLEANUP_REVERT: 'Hoàn tác chuẩn hoá dữ liệu dịch vụ kèm theo',
      PRICING_ADDON_TLD_APPLY_DEFAULTS: 'Bật các dịch vụ kèm theo mặc định cho TLD',
      PRICING_ADDON_TLD_SET: 'Đặt chế độ dịch vụ kèm theo cho TLD',
      PRICING_COST_COMPONENT_END: 'Kết thúc thành phần phí nhà đăng ký gốc',
      PRICING_COST_COMPONENT_PUBLISH: 'Công bố thành phần phí nhà đăng ký gốc',
      TLD_DEFAULT_NAMESERVERS_SET: 'Cập nhật máy chủ tên mặc định khi đăng ký',
      TLD_PARKING_NAMESERVERS_SET: 'Cập nhật máy chủ tên tạm khi hết hạn',
      TLD_SETTINGS_PUBLISH: 'Công bố cấu hình TLD',
    },
    source: {
      ADMIN_API: 'Control Center',
      API: 'Cổng khách hàng hoặc tích hợp API',
      WORKER: 'Hệ thống tự động xử lý',
      DOMAIN_WORKER: 'Hệ thống tự động xử lý',
      LIFECYCLE: 'Hệ thống tự động xử lý',
      LIFECYCLE_WORKER: 'Hệ thống tự động xử lý',
      JOB_WORKER: 'Hệ thống tự động xử lý',
      SCHEDULER: 'Xử lý theo lịch',
      EPP_POLL_WORKER: 'Thông báo từ registry',
      EPP_GATEWAY: 'Kết nối registry',
      RECONCILIATION: 'Đối soát registry',
      RECONCILIATION_WORKER: 'Đối soát registry',
      DOSSIER: 'Xử lý hồ sơ',
      CASES: 'Xử lý vụ việc tuân thủ',
      VNNIC_SYNC_WORKER: 'Đồng bộ VNNIC',
      VNNIC_RECONCILIATION_WORKER: 'Đối soát VNNIC',
      VNNIC_REPORT_WORKER: 'Báo cáo VNNIC',
      IDENTITY_WORKER: 'Xác minh danh tính',
      DROPCATCH_WORKER: 'Giành đăng ký tên miền được giải phóng',
      OBSERVABILITY_API: 'Giám sát',
      DEV_SEED: 'Dữ liệu mẫu',
    },
    epp: {
      DOMAIN_CHECK: 'Kiểm tra tên miền',
      DOMAIN_INFO: 'Tra cứu tên miền',
      DOMAIN_CREATE: 'Đăng ký tên miền',
      DOMAIN_RENEW: 'Gia hạn tên miền',
      DOMAIN_UPDATE: 'Cập nhật tên miền',
      DOMAIN_UPDATE_STATUS: 'Cập nhật trạng thái tên miền',
      DOMAIN_UPDATE_REGISTRANT: 'Cập nhật chủ thể',
      DOMAIN_UPDATE_AUTHINFO: 'Cập nhật mã AuthInfo',
      DOMAIN_DELETE: 'Xoá tên miền',
      DOMAIN_RESTORE: 'Khôi phục tên miền',
      DOMAIN_DISPUTE: 'Tranh chấp tên miền',
      DOMAIN_TRANSFER_REQUEST: 'Yêu cầu chuyển đổi',
      DOMAIN_TRANSFER_QUERY: 'Tra cứu chuyển đổi',
      DOMAIN_TRANSFER_APPROVE: 'Chấp thuận chuyển đổi',
      DOMAIN_TRANSFER_REJECT: 'Từ chối chuyển đổi',
      DOMAIN_TRANSFER_CANCEL: 'Huỷ chuyển đổi',
      CONTACT_CHECK: 'Kiểm tra liên hệ',
      CONTACT_INFO: 'Tra cứu liên hệ',
      CONTACT_CREATE: 'Tạo liên hệ',
      CONTACT_UPDATE: 'Cập nhật liên hệ',
      CONTACT_DELETE: 'Xoá liên hệ',
      CONTACT_TRANSFER: 'Chuyển liên hệ',
      HOST_CHECK: 'Kiểm tra máy chủ tên',
      HOST_INFO: 'Tra cứu máy chủ tên',
      HOST_CREATE: 'Tạo máy chủ tên',
      HOST_UPDATE: 'Cập nhật máy chủ tên',
      HOST_DELETE: 'Xoá máy chủ tên',
      POLL_REQUEST: 'Lấy thông báo poll',
      POLL_ACK: 'Xác nhận thông báo poll',
      LOGIN: 'Đăng nhập phiên EPP',
      LOGOUT: 'Đăng xuất phiên EPP',
      HELLO: 'Chào phiên EPP',
    },
    order: {
      withOthers: '{{domain}} +{{n}} tên miền · {{date}}',
      single: '{{domain}} · {{date}}',
      dateOnly: 'Đơn hàng {{date}}',
    },
  },
  range: {
    label: 'Khoảng thời gian',
    last: '{{value}} gần nhất',
    custom: 'Tuỳ chọn',
    from: 'Từ (UTC)',
    to: 'Đến (UTC)',
    presets: { '5m': '5 phút', '15m': '15 phút', '1h': '1 giờ', '6h': '6 giờ', '24h': '24 giờ', '7d': '7 ngày', '30d': '30 ngày' },
    invalid: 'Thời điểm bắt đầu và kết thúc phải hợp lệ.',
    order: 'Thời điểm kết thúc phải sau thời điểm bắt đầu.',
    tooLong: 'Tối đa {{days}} ngày cho mỗi truy vấn.',
  },
};
